import Logger from "./Logger";
import hark from "./../helpers/hark";
import { getSignalingUrl } from "../utils/urlFactory";
import * as requestActions from "../actions/requestActions";
import * as meActions from "../actions/meActions";
import * as roomActions from "../actions/roomActions";
import * as peerActions from "../actions/peerActions";
import * as peerVolumeActions from "../actions/peerVolumeActions";
import * as settingsActions from "../actions/settingsActions";
import * as chatActions from "../actions/chatActions";
import * as privateChatActions from "../actions/privateChatActions";
import * as toolAreaActions from "../actions/toolareaActions";
import * as whiteBoardActions from "../actions/whiteBoardActions";
import * as lobbyPeerActions from "../actions/lobbyPeerActions";
import * as consumerActions from "../actions/consumerActions";
import * as producerActions from "../actions/producerActions";
import * as notificationActions from "../actions/notificationActions";
import * as meetingManagementActions from "../actions/Management/meetingManagementAction";
import * as remoteControlActions from "../actions/remoteControlActions";
import RecordRTC from "recordrtc";
import * as surveyActions from "../actions/surveyActions";
import { transcriptGenerator } from "../utils/transcriptGenerator";
import isElectron from "is-electron";
import { setPresenceStatus } from "../actions/userActions";
import { isMeetingRoomPath } from "../utils/portalPaths";
import {
  getClientInfo,
  REMOTE_CONTROL_ERRORS,
} from "./remoteControl/clientInfo";
import {
  decodeRemoteControlEvent,
  encodeRemoteControlEvent,
  INPUT_LABEL,
  isBlockedHotkey,
  isRemoteControlLabel,
  MOTION_INTERVAL_MS,
  MOTION_LABEL,
} from "./remoteControl/protocol";
import { rcLog, waitForDataProducerOpen } from "./remoteControl/debug";

let saveAs;
let mediasoupClient;
let io;
let ScreenShare;
let Spotlights;

let turnServers,
  requestTimeout,
  transportOptions,
  lastN,
  mobileLastN,
  defaultResolution;

if (process.env.NODE_ENV !== "test") {
  ({
    turnServers,
    requestTimeout,
    transportOptions,
    lastN,
    mobileLastN,
    defaultResolution,
  } = window.config);
}

const logger = new Logger("RoomClient");

const ROOM_OPTIONS = {
  requestTimeout: requestTimeout,
  transportOptions: transportOptions,
  turnServers: turnServers,
};

const VIDEO_CONSTRAINS = {
  low: {
    width: { ideal: 320 },
    height: { ideal: 240 },
    //aspectRatio: 1.334,
    //frameRate: { ideal: 15, max: 20 },
  },
  medium: {
    width: { ideal: 480 },
    height: { ideal: 360 },
  },
  high: {
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
  veryhigh: {
    width: { ideal: 1280 },
    height: { ideal: 960 },
  },
  ultra: {
    width: { ideal: 1920 },
    height: { ideal: 1440 },
  },
};

const PC_PROPRIETARY_CONSTRAINTS = {
  optional: [{ googDscp: true }],
};

const VIDEO_SIMULCAST_ENCODINGS = [
  { scaleResolutionDownBy: 4 },
  { scaleResolutionDownBy: 2 },
  { scaleResolutionDownBy: 1 },
];

// Used for VP9 webcam video.
const VIDEO_KSVC_ENCODINGS = [{ scalabilityMode: "S3T3_KEY" }];

// Used for VP9 desktop sharing.
const VIDEO_SVC_ENCODINGS = [{ scalabilityMode: "S3T3", dtx: true }];

function getConferenceConfig() {
  const c =
    (typeof window !== "undefined" &&
      window.config &&
      window.config.conferenceSettings) ||
    {};
  return {
    defaultResolution:
      c.defaultResolution ||
      (window.config && window.config.defaultResolution) ||
      "veryhigh",
    preferredVideoCodec:
      (c.preferredVideoCodec ||
        (window.config && window.config.preferredVideoCodec) ||
        "auto"
      ).toLowerCase(),
    screenShareWidth:
      Number(c.screenShareWidth || window.config?.screenShareWidth) || 1920,
    screenShareHeight:
      Number(c.screenShareHeight || window.config?.screenShareHeight) || 1080,
    screenShareFrameRate:
      Number(c.screenShareFrameRate || window.config?.screenShareFrameRate) ||
      5,
    webcamFrameRate:
      Number(c.webcamFrameRate || window.config?.webcamFrameRate) || 15,
  };
}

function pickPreferredVideoCodec(mediasoupDevice, preferred) {
  if (!preferred || preferred === "auto" || !mediasoupDevice) return undefined;
  const want = `video/${preferred}`.toLowerCase();
  const codecs =
    (mediasoupDevice.rtpCapabilities &&
      mediasoupDevice.rtpCapabilities.codecs) ||
    [];
  return codecs.find(
    (c) => c.kind === "video" && String(c.mimeType).toLowerCase() === want
  );
}

let store;

let intl;

export default class RoomClient {
  /**
   * @param  {Object} data
   * @param  {Object} data.store - The Redux store.
   * @param  {Object} data.intl - react-intl object
   */
  static init(data) {
    store = data.store;
    intl = data.intl;
  }

  constructor({
    peerId,
    accessCode,
    device,
    useSimulcast,
    useSharingSimulcast,
    produce,
    forceTcp,
    displayName,
    muted,
    userToken,
  } = {}) {
    if (!peerId) throw new Error("Missing peerId");
    else if (!device) throw new Error("Missing device");
    logger.debug(
      'constructor() [peerId: "%s", device: "%s", useSimulcast: "%s", produce: "%s", forceTcp: "%s", displayName ""]',
      peerId,
      device.flag,
      useSimulcast,
      produce,
      forceTcp,
      displayName
    );

    this._signalingUrl = null;

    // Closed flag.
    this._closed = false;

    // Whether we should produce.
    this._produce = produce;

    // Wheter we force TCP
    this._forceTcp = forceTcp;

    // Use displayName
    if (displayName)
      store.dispatch(settingsActions.setDisplayName(displayName));

    // Whether simulcast should be used.
    this._useSimulcast = useSimulcast;

    if ("simulcast" in window.config)
      this._useSimulcast = window.config.simulcast;

    // Whether simulcast should be used for sharing
    this._useSharingSimulcast = useSharingSimulcast;

    if ("simulcastSharing" in window.config)
      this._useSharingSimulcast = window.config.simulcastSharing;

    this._muted = muted;

    this._userToken = userToken;

    // This device
    this._device = device;

    // My peer name.
    this._peerId = peerId;

    // Access code
    this._accessCode = accessCode;

    // Alert sound
    this._soundAlert = new Audio("/sounds/notify.ogg");

    // Socket.io peer connection
    this._signalingSocket = null;

    this._liveStreamingSocket = null;

    // The room ID
    this._roomId = null;

    // mediasoup-client Device instance.
    // @type {mediasoupClient.Device}
    this._mediasoupDevice = null;

    // Put the browser info into state
    store.dispatch(meActions.setBrowser(device));

    {
      const conf = getConferenceConfig();
      const res =
        conf.defaultResolution || defaultResolution || "veryhigh";
      store.dispatch(settingsActions.setVideoResolution(res));
    }

    // Max spotlights (brand / process.js — oda açılırken güncel config)
    if (device.browser.getPlatformType() === "desktop")
      this._maxSpotlights =
        Number(window.config && window.config.lastN) || lastN || 10;
    else
      this._maxSpotlights =
        Number(window.config && window.config.mobileLastN) ||
        mobileLastN ||
        3;

    store.dispatch(settingsActions.setLastN(this._maxSpotlights));

    // Manager of spotlight
    this._spotlights = null;

    // Transport for sending.
    this._sendTransport = null;
    this._sendTransportHasSctp = false;

    // Transport for receiving.
    this._recvTransport = null;

    // Local mic mediasoup Producer.
    this._micProducer = null;

    // Local mic hark
    this._hark = null;

    // Local webcam mediasoup Producer.
    this._webcamProducer = null;

    // Map of webcam MediaDeviceInfos indexed by deviceId.
    // @type {Map<String, MediaDeviceInfos>}
    this._webcams = {};

    this._audioDevices = {};

    this._audioOutputDevices = {};

    // mediasoup Consumers.
    // @type {Map<String, mediasoupClient.Consumer>}
    this._consumers = new Map();

    this._dataConsumers = new Map();

    this._remoteControlHeartbeat = null;
    this._remoteControlMotionProducer = null;
    this._remoteControlInputProducer = null;
    this._remoteControlProducingSessionId = null;
    this._remoteControlProducePromise = null;
    this._remoteControlSeq = 0;
    this._remoteControlLastMotionAt = 0;

    this._screenSharingForRecorder = null;

    this._screenSharing = null;

    this._screenSharingProducer = null;

    this._localStreams = [];

    this._startKeyListener();

    this._startDevicesListener();

    this._startRemoteControlEmergencyListener();
  }

  clearStates() {
    store.dispatch(roomActions.clearState());
    store.dispatch(meActions.clearState());
    store.dispatch(producerActions.clearState());
    store.dispatch(peerActions.clearState());
    store.dispatch(lobbyPeerActions.clearState());
    store.dispatch(consumerActions.clearState());
    store.dispatch(peerVolumeActions.clearState());
    store.dispatch(chatActions.clearState());
    store.dispatch(privateChatActions.clearState());
    store.dispatch(toolAreaActions.clearState());
    store.dispatch(whiteBoardActions.clearState());
    store.dispatch(surveyActions.clearState());
    store.dispatch(remoteControlActions.clearRemoteControl());
  }

  _stopProducerTrack(producer) {
    if (!producer || !producer.track) return;

    try {
      producer.track.stop();
    } catch (error) {}
  }

  _stopAllLocalMediaTracks() {
    const stopProducer = (producer, field) => {
      if (!producer) return;

      this._stopProducerTrack(producer);

      try {
        producer.close();
      } catch (error) {}

      store.dispatch(producerActions.removeProducer(producer.id));
      this[field] = null;
    };

    stopProducer(this._micProducer, "_micProducer");
    stopProducer(this._webcamProducer, "_webcamProducer");
    stopProducer(this._screenSharingProducer, "_screenSharingProducer");

    if (this._hark != null) {
      try {
        this._hark.stop();
      } catch (error) {}
      this._hark = null;
    }

    this._localStreams.forEach((stream) => {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (error) {}
      });
    });

    this._localStreams = [];
  }

  close(navigateToHomeScreen = true) {
    this._roomJoining = false;
    this._roomJoined = false;

    if (this._closed) return;
    this._closed = true;

    if (this._recording) this.stopRecording();

    logger.debug("close()");

    this._stopAllLocalMediaTracks();
    this._call = false;
    this._joinScreen = false;

    this._stopRemoteControlHeartbeat();
    this._closeRemoteControlDataProducers();

    if (this._signalingSocket) {
      try {
        this._signalingSocket.close();
      } catch (e) {
        /* ignore */
      }
      this._signalingSocket = null;
    }

    // Close mediasoup Transports.
    if (this._sendTransport) {
      try {
        this._sendTransport.close();
      } catch (e) {
        /* ignore */
      }
      this._sendTransport = null;
    }

    if (this._recvTransport) {
      try {
        this._recvTransport.close();
      } catch (e) {
        /* ignore */
      }
      this._recvTransport = null;
    }

    store.dispatch(roomActions.setRoomState("closed"));
    this.clearStates();

    // clearStates me.uiStep'i sıfırlar — portal mesajlaşmaya dön
    store.dispatch(meActions.setUIStep("MANAGEMENT_SCREEN"));
    store.dispatch(meActions.setMainMenuSelection("MESSAGING"));
    try {
      store.dispatch(setPresenceStatus({ status: "online" }));
    } catch (e) {
      /* presence optional */
    }

    const pathname =
      typeof window !== "undefined" ? window.location.pathname || "/" : "/";
    const leaveRoomPath = isMeetingRoomPath(pathname);
    // navigateToHomeScreen=false (arama bitişi) olsa bile /:roomId'de kalınmamalı;
    // aksi halde Steps CamMicSetupDialog açar.
    if (navigateToHomeScreen || leaveRoomPath) {
      const target =
        (window.config && window.config.redirectUserToUrlWhenConferenceEnded) ||
        "/messaging";
      const dest =
        target === "/" || !target ? "/messaging" : target;
      if (pathname !== dest) {
        window.location.assign(dest);
      }
    }
  }

  exitFromLobby() {
    logger.debug("exitFromLobby()");

    this._signalingSocket && this._signalingSocket.close();

    // Close mediasoup Transports.
    if (this._sendTransport) this._sendTransport.close();

    if (this._recvTransport) this._recvTransport.close();

    store.dispatch(roomActions.setRoomState("closed"));
  }

  downloadTranscript() {
    this.sendRequest("transcript", null);
  }

  _startKeyListener() {
    // Add keypress event listner on document
    document.addEventListener("keypress", (event) => {
      const webinar = store.getState().room.webinar;
      if (webinar || !this._roomId || !store.getState().room.joined) return;
      const key = String.fromCharCode(event.which);

      const source = event.target;

      const exclude = ["input", "textarea"];

      if (exclude.indexOf(source.tagName.toLowerCase()) === -1) {
        logger.debug('keyPress() [key:"%s"]', key);

        switch (key) {
          case "1": {
            // Set democratic view
            store.dispatch(roomActions.setDisplayMode("democratic"));
            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.setDemocraticView",
                  defaultMessage: "Changed layout to democratic view",
                }),
              })
            );
            break;
          }

          case "2": {
            // Set filmstrip view
            store.dispatch(roomActions.setDisplayMode("filmstrip"));
            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.setFilmStripView",
                  defaultMessage: "Changed layout to filmstrip view",
                }),
              })
            );
            break;
          }

          case " ":
          case "m": {
            // Toggle microphone
            if (this._micProducer) {
              if (!this._micProducer.paused) {
                this.muteMic();

                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "devices.microPhoneMute",
                      defaultMessage: "Muted your microphone",
                    }),
                  })
                );
              } else {
                this.unmuteMic();

                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "devices.microPhoneUnMute",
                      defaultMessage: "Unmuted your microphone",
                    }),
                  })
                );
              }
            } else {
              this.enableMic();

              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "devices.microphoneEnable",
                    defaultMessage: "Enabled your microphone",
                  }),
                })
              );
            }

            break;
          }

          case "v": {
            // Toggle video
            if (this._webcamProducer) this.disableWebcam();
            else this.enableWebcam();

            break;
          }

          case "t": {
            // Toggle dark theme
            store.dispatch(settingsActions.toggleDarkMode());

            break;
          }

          default: {
            break;
          }
        }
      }
    });
  }

  _startDevicesListener() {
    navigator.mediaDevices.addEventListener("devicechange", async () => {
      logger.debug(
        "_startDevicesListener() | navigator.mediaDevices.ondevicechange"
      );

      await this._updateAudioDevices();
      await this._updateWebcams();
      await this._updateAudioOutputDevices();

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage({
            id: "devices.devicesChanged",
            defaultMessage:
              "Your devices changed, configure your devices in the settings dialog",
          }),
        })
      );
    });
  }

  _soundNotification() {
    if (!store.getState().settings.soundNotificationsDisabled) {
      const alertPromise = this._soundAlert.play();

      if (alertPromise !== undefined) {
        alertPromise.then().catch((error) => {
          logger.error("_soundAlert.play() | failed: %o", error);
        });
      }
    }
  }

  notify(text) {
    store.dispatch(requestActions.notify({ text: text }));
  }

  timeoutCallback(callback) {
    let called = false;

    const interval = setTimeout(() => {
      if (called) return;
      called = true;
      callback(new Error("Request timeout."));
    }, ROOM_OPTIONS.requestTimeout);

    return (...args) => {
      if (called) return;
      called = true;
      clearTimeout(interval);

      callback(...args);
    };
  }

  sendRequest(method, data) {
    return new Promise((resolve, reject) => {
      if (!this._signalingSocket) {
        reject("No socket connection.");
      } else {
        this._signalingSocket.emit(
          "request",
          { method, data },
          this.timeoutCallback((err, response) => {
            if (err) {
              reject(err);
            } else {
              resolve(response);
            }
          })
        );
      }
    });
  }

  async changeDisplayName(displayName) {
    logger.debug('changeDisplayName() [displayName:"%s"]', displayName);

    if (!displayName) displayName = "Katılımcı";

    store.dispatch(meActions.setDisplayNameInProgress(true));

    try {
      await this.sendRequest("changeDisplayName", {
        displayName,
      });

      store.dispatch(settingsActions.setDisplayName(displayName));

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage(
            {
              id: "room.changedDisplayName",
              defaultMessage: "Your display name changed to {displayName}",
            },
            {
              displayName,
            }
          ),
        })
      );
    } catch (error) {
      logger.error("changeDisplayName() | failed: %o", error);

      /*			store.dispatch(requestActions.notify(
        {
          type : 'error',
          text : intl.formatMessage({
            id             : 'room.changeDisplayNameError',
            defaultMessage : 'An error occurred while changing your display name'
          })
        }));*/
    }

    store.dispatch(meActions.setDisplayNameInProgress(false));
  }

  async changePicture(picture) {
    logger.debug('changePicture() [picture: "%s"]', picture);

    try {
      await this.sendRequest("changePicture", { picture });
    } catch (error) {
      logger.error("changePicture() | failed: %o", error);
    }
  }

  async sendChatMessage(chatMessage) {
    logger.debug('sendChatMessage() [chatMessage:"%s"]', chatMessage);

    chatMessage.senderId = this._peerId;

    try {
      store.dispatch(
        chatActions.addUserMessage(
          chatMessage.text,
          store.getState().settings.displayName,
          this._peerId
        )
      );

      await this.sendRequest("chatMessage", { chatMessage });
    } catch (error) {
      logger.error("sendChatMessage() | failed: %o", error);

      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.chatError",
            defaultMessage: "Unable to send chat message",
          }),
        })
      );
    }
  }

  async sendPrivateChatMessage(chatMessage) {
    logger.debug('sendPrivateChatMessage() [chatMessage:"%s"]', chatMessage);

    chatMessage.senderId = this._peerId;

    try {
      store.dispatch(
        privateChatActions.addPrivateUserMessage(
          chatMessage.text,
          store.getState().settings.displayName,
          this._peerId,
          chatMessage.selectedPeerId
        )
      );

      await this.sendRequest("privateChatMessage", { chatMessage });
    } catch (error) {
      logger.error("sendPrivateChatMessage() | failed: %o", error);

      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.chatError",
            defaultMessage: "Unable to send chat message",
          }),
        })
      );
    }
  }

  async sendMeetingDetails(meetingDetails) {
    logger.debug('sendMeetingDetails() [meetingDetails:"%s"]', meetingDetails);

    try {
      await this.sendRequest("meetingDetails", { meetingDetails });
    } catch (error) {
      logger.error("sendMeetingDetails() | failed: %o", error);

      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.meetingDetailsError",
            defaultMessage: "Unable to send meeting details",
          }),
        })
      );
    }
  }

  async sendWhiteBoardEvent(event) {
    logger.debug("sendWhiteBoardEvent()");

    try {
      await this.sendRequest("whiteBoardEvent", { event });
    } catch (error) {
      logger.error("sendWhiteBoardEvent() | failed: %o", error);
    }
  }

  _notifyRemoteControlError(error) {
    const code =
      (error && (error.message || error.code || error)) || "invalid_state";
    const normalized =
      typeof code === "string"
        ? code.replace(/^Error:\s*/i, "").trim()
        : code;
    const id =
      REMOTE_CONTROL_ERRORS[normalized] || "remoteControl.error.generic";

    rcLog("error", { code: normalized, error });

    store.dispatch(
      requestActions.notify({
        type: "error",
        text: intl.formatMessage({
          id,
          defaultMessage: "Remote control request failed",
        }),
      })
    );
  }

  _applyRemoteControlSession(session, reason) {
    rcLog("session", { session, reason });

    store.dispatch(
      remoteControlActions.setRemoteControlSession(session, reason)
    );
    this._syncRemoteControlHostSession(session);

    const meId = store.getState().me.id;

    if (
      session &&
      session.status === "active" &&
      session.controllerPeerId === meId
    ) {
      this._startRemoteControlHeartbeat();
      this._ensureRemoteControlDataProducers(session).catch((error) => {
        logger.error("remoteControl produceData failed: %o", error);
      });
    } else {
      this._stopRemoteControlHeartbeat();
      this._closeRemoteControlDataProducers();
    }
  }

  _syncRemoteControlHostSession(session) {
    const desktop = window.wunityDesktop;

    if (!desktop) return;

    const meId = store.getState().me.id;

    if (
      session &&
      session.status === "active" &&
      session.hostPeerId === meId &&
      desktop.setRemoteControlSession
    ) {
      desktop.setRemoteControlSession({
        sessionId: session.sessionId,
        bounds: session.bounds,
      });
    } else if (desktop.clearRemoteControlSession) {
      desktop.clearRemoteControlSession();
    }
  }

  async _ensureRemoteControlDataProducers(session) {
    if (!session || !session.sessionId) return;

    try {
      await this._ensureSendTransport();

      if (!this._sendTransport) {
        rcLog("missing-send-transport");
        throw new Error("send transport unavailable");
      }

      if (this._remoteControlProducePromise) {
        await this._remoteControlProducePromise;
      }

      if (
        this._remoteControlProducingSessionId === session.sessionId &&
        this._remoteControlMotionProducer &&
        this._remoteControlInputProducer
      ) {
        return;
      }

      this._remoteControlProducePromise = (async () => {
        await this._closeRemoteControlDataProducers();
        this._remoteControlProducingSessionId = session.sessionId;

        rcLog("produceData:start", { sessionId: session.sessionId });

        this._remoteControlMotionProducer = await this._sendTransport.produceData({
          ordered: false,
          maxRetransmits: 0,
          label: MOTION_LABEL,
          protocol: "json",
          appData: {
            sessionId: session.sessionId,
            hostPeerId: session.hostPeerId,
          },
        });

        this._remoteControlInputProducer = await this._sendTransport.produceData({
          ordered: true,
          label: INPUT_LABEL,
          protocol: "json",
          appData: {
            sessionId: session.sessionId,
            hostPeerId: session.hostPeerId,
          },
        });

        await Promise.all([
          waitForDataProducerOpen(this._remoteControlMotionProducer),
          waitForDataProducerOpen(this._remoteControlInputProducer),
        ]);

        rcLog("produceData:ready", {
          motion: this._remoteControlMotionProducer.id,
          input: this._remoteControlInputProducer.id,
        });
      })();

      await this._remoteControlProducePromise;
    } catch (error) {
      rcLog("produceData:failed", error);
      this._notifyRemoteControlError(error);
    } finally {
      this._remoteControlProducePromise = null;
    }
  }

  async _ensureSendTransport() {
    if (!this._mediasoupDevice) return;

    if (this._sendTransport && !this._sendTransportHasSctp) {
      rcLog("recreate-send-transport-for-sctp");
      this._sendTransport.close();
      this._sendTransport = null;
      this._sendTransportHasSctp = false;
    }

    if (this._sendTransport) return;

    rcLog("create-send-transport");

    const transportInfo = await this.sendRequest("createWebRtcTransport", {
      forceTcp: this._forceTcp,
      producing: true,
      consuming: false,
      enableSctp: true,
    });

    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
    } = transportInfo;

    this._sendTransportHasSctp = Boolean(sctpParameters);

    this._sendTransport = this._mediasoupDevice.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: ROOM_OPTIONS.turnServers,
      proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
    });

    this._sendTransport.on(
      "connect",
      ({ dtlsParameters }, callback, errback) => {
        this.sendRequest("connectWebRtcTransport", {
          transportId: this._sendTransport.id,
          dtlsParameters,
        })
          .then(callback)
          .catch(errback);
      }
    );

    this._sendTransport.on(
      "produce",
      async ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          const { id: producerId } = await this.sendRequest("produce", {
            transportId: this._sendTransport.id,
            kind,
            rtpParameters,
            appData,
          });

          callback({ id: producerId });
        } catch (error) {
          errback(error);
        }
      }
    );

    this._sendTransport.on(
      "producedata",
      async (
        { sctpStreamParameters, label, protocol, appData },
        callback,
        errback
      ) => {
        try {
          const { id: producerId } = await this.sendRequest("produceData", {
            transportId: this._sendTransport.id,
            sctpStreamParameters,
            label,
            protocol,
            appData,
          });

          callback({ id: producerId });
        } catch (error) {
          errback(error);
        }
      }
    );

    this._sendTransport.on("connectionstatechange", (connectState) => {
      rcLog("send-transport-state", connectState);
      switch (connectState) {
        case "disconnected":
        case "failed":
          this.restartIce();
          break;
        default:
          break;
      }
    });
  }

  async _closeRemoteControlDataProducers() {
    this._remoteControlProducingSessionId = null;

    const motion = this._remoteControlMotionProducer;
    const input = this._remoteControlInputProducer;

    this._remoteControlMotionProducer = null;
    this._remoteControlInputProducer = null;

    [motion, input].forEach((producer) => {
      if (!producer) return;

      try {
        producer.close();
      } catch (error) {}
    });
  }

  sendRemoteControlMotion(partial) {
    this._sendRemoteControlEvent(
      this._remoteControlMotionProducer,
      partial,
      partial && partial.t === "m"
    );
  }

  sendRemoteControlInput(partial) {
    this._sendRemoteControlEvent(
      this._remoteControlInputProducer,
      partial,
      false
    );
  }

  _sendRemoteControlEvent(producer, partial, throttle) {
    const session = store.getState().remoteControl;

    if (
      !producer ||
      producer.closed ||
      !session ||
      session.status !== "active" ||
      !session.sessionId
    ) {
      return;
    }

    if (producer.readyState && producer.readyState !== "open") {
      rcLog("send-skipped-not-open", {
        label: producer.label,
        readyState: producer.readyState,
        type: partial && partial.t,
      });
      return;
    }

    if (throttle) {
      const now = Date.now();

      if (now - this._remoteControlLastMotionAt < MOTION_INTERVAL_MS) return;

      this._remoteControlLastMotionAt = now;
    }

    this._remoteControlSeq += 1;

    try {
      producer.send(
        encodeRemoteControlEvent({
          ...partial,
          sid: session.sessionId,
          seq: this._remoteControlSeq,
        })
      );
    } catch (error) {
      logger.error("remoteControl send failed: %o", error);
    }
  }

  _onRemoteControlData(raw) {
    const event = decodeRemoteControlEvent(raw);

    if (!event) {
      rcLog("host-decode-failed", raw);
      return;
    }

    const session = store.getState().remoteControl;
    const meId = store.getState().me.id;

    if (
      !session ||
      session.status !== "active" ||
      session.hostPeerId !== meId ||
      event.sid !== session.sessionId
    ) {
      rcLog("host-drop-event", { event, session, meId });
      return;
    }

    if (isBlockedHotkey(event)) return;

    rcLog("host-inject", event.t);

    if (window.wunityDesktop && window.wunityDesktop.injectRemoteControl) {
      window.wunityDesktop.injectRemoteControl(event);
    }
  }

  _startRemoteControlHeartbeat() {
    if (this._remoteControlHeartbeat) return;

    this._remoteControlHeartbeat = setInterval(() => {
      const session = store.getState().remoteControl;

      if (
        session.status !== "active" ||
        session.controllerPeerId !== store.getState().me.id ||
        !session.sessionId
      ) {
        this._stopRemoteControlHeartbeat();
        return;
      }

      this.sendRequest("remoteControl:heartbeat", {
        sessionId: session.sessionId,
      }).catch((error) => {
        logger.error("remoteControl heartbeat failed: %o", error);
      });
    }, 2000);
  }

  _stopRemoteControlHeartbeat() {
    if (!this._remoteControlHeartbeat) return;

    clearInterval(this._remoteControlHeartbeat);
    this._remoteControlHeartbeat = null;
  }

  _startRemoteControlEmergencyListener() {
    if (!window.wunityDesktop || !window.wunityDesktop.onEmergencyStop) return;

    window.wunityDesktop.onEmergencyStop(() => {
      this.endRemoteControl("local_emergency").catch(() => {});
    });
  }

  async requestRemoteControl(hostPeerId) {
    logger.debug('requestRemoteControl() [hostPeerId:"%s"]', hostPeerId);

    try {
      const session = await this.sendRequest("remoteControl:request", {
        hostPeerId,
      });

      this._applyRemoteControlSession(session);
    } catch (error) {
      logger.error("requestRemoteControl() | failed: %o", error);
      this._notifyRemoteControlError(error);
    }
  }

  async acceptRemoteControl() {
    const session = store.getState().remoteControl;

    if (!session.sessionId) return;

    logger.debug("acceptRemoteControl()");

    try {
      let bounds = null;

      if (window.wunityDesktop && window.wunityDesktop.getPrimaryDisplayBounds) {
        bounds = await window.wunityDesktop.getPrimaryDisplayBounds();
      }

      const next = await this.sendRequest("remoteControl:accept", {
        sessionId: session.sessionId,
        bounds,
      });

      this._applyRemoteControlSession(next);
    } catch (error) {
      logger.error("acceptRemoteControl() | failed: %o", error);
      this._notifyRemoteControlError(error);
    }
  }

  async rejectRemoteControl() {
    const session = store.getState().remoteControl;

    if (!session.sessionId) return;

    logger.debug("rejectRemoteControl()");

    try {
      await this.sendRequest("remoteControl:reject", {
        sessionId: session.sessionId,
        reason: "declined",
      });
    } catch (error) {
      logger.error("rejectRemoteControl() | failed: %o", error);
      this._notifyRemoteControlError(error);
    }
  }

  async endRemoteControl(reason = "controller_stop") {
    const session = store.getState().remoteControl;

    if (!session.sessionId || session.status === "idle") return;

    logger.debug("endRemoteControl()");

    try {
      await this.sendRequest("remoteControl:end", {
        sessionId: session.sessionId,
        reason,
      });
    } catch (error) {
      logger.error("endRemoteControl() | failed: %o", error);
    }
  }

  async publishSurvey(survey) {
    logger.debug("publishSurvey()");

    try {
      await this.sendRequest("publishSurvey", { survey });
    } catch (error) {
      logger.error("publishSurvey() | failed: %o", error);
    }
  }

  async publishSurveyAnswer(answer) {
    logger.debug("publishSurveyAnswer()");

    try {
      await this.sendRequest("publishSurveyAnswer", { answer });
    } catch (error) {
      logger.error("publishSurveyAnswer() | failed: %o", error);
    }
  }

  saveFile(file) {
    file.getBlob((err, blob) => {
      if (err) {
        return store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "filesharing.saveFileError",
              defaultMessage: "Unable to save file",
            }),
          })
        );
      }

      saveAs(blob, file.name);
    });
  }

  async getServerHistory(joinAudio, joinVideo, joinScreen = false) {
    logger.debug("getServerHistory()");

    try {
      const {
        roomName,
        chatHistory,
        privateChatHistory,
        lastNHistory,
        locked,
        lobbyPeers,
        accessCode,
        webinar,
        recordingNotificationStatus,
        liveStreamingNotificationStatus,
        whiteBoardData,
        survey,
        startTime,
        creatorId,
      } = await this.sendRequest("serverHistory");
      this._creatorId = creatorId;
      store.dispatch(roomActions.setRoomName(roomName));

      if (webinar) {
        store.dispatch(roomActions.setRoomWebinar(true));
        store.dispatch(roomActions.setDisplayMode("filmstrip"));
      }
      if (whiteBoardData) {
        store.dispatch(whiteBoardActions.setData(whiteBoardData));
      }

      if (survey) {
        store.dispatch(surveyActions.setCurrentSurvey(survey));
      }

      chatHistory.length > 0 &&
        store.dispatch(chatActions.addChatHistory(chatHistory));

      if (privateChatHistory) {
        let privChat =
          privateChatHistory === "{}"
            ? new Map()
            : new Map(Object.entries(privateChatHistory));
        store.dispatch(privateChatActions.addPrivateChatHistory(privChat));
      }

      if (lastNHistory.length > 0) {
        logger.debug("Got lastNHistory");

        const index = lastNHistory.indexOf(this._peerId);

        lastNHistory.splice(index, 1);

        this._spotlights.addSpeakerList(lastNHistory);
      }

      locked
        ? store.dispatch(roomActions.setRoomLocked())
        : store.dispatch(roomActions.setRoomUnLocked());

      if (!webinar) {
        // Don't produce if explicitely requested to not to do it.
        if (this._produce) {
          if (joinAudio && this._mediasoupDevice.canProduce("audio"))
            if (!this._muted) this.enableMic();

          if (joinVideo && this._mediasoupDevice.canProduce("video"))
            this.enableWebcam();

          if (joinScreen || this._joinScreen) {
            // Transport hazır olduktan sonra ekran paylaşımını başlat.
            setTimeout(() => {
              this.enableScreenSharing().catch((error) => {
                logger.error(
                  "auto enableScreenSharing() failed: %o",
                  error
                );
              });
            }, 400);
          }
        }
      }

      lobbyPeers.length > 0 &&
        lobbyPeers.forEach((peer) => {
          store.dispatch(lobbyPeerActions.addLobbyPeer(peer.peerId));
          store.dispatch(
            lobbyPeerActions.setLobbyPeerDisplayName(
              peer.displayName,
              peer.peerId
            )
          );
          store.dispatch(lobbyPeerActions.setLobbyPeerPicture(peer.picture));
        });

      accessCode != null &&
        store.dispatch(roomActions.setAccessCode(accessCode));
      if (startTime) {
        store.dispatch(roomActions.setStartTime(startTime));
      }

      store.dispatch(
        roomActions.setRecordingNotificationStatus(recordingNotificationStatus)
      );

      store.dispatch(
        roomActions.setLiveStreamingNotificationStatus(
          liveStreamingNotificationStatus
        )
      );
    } catch (error) {
      logger.error("getServerHistory() | failed: %o", error);
    }
  }

  async muteMic() {
    logger.debug("muteMic()");

    this._micProducer.pause();

    try {
      await this.sendRequest("pauseProducer", {
        producerId: this._micProducer.id,
      });

      store.dispatch(producerActions.setProducerPaused(this._micProducer.id));
    } catch (error) {
      logger.error("muteMic() | failed: %o", error);

      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "devices.microphoneMuteError",
            defaultMessage: "Unable to mute your microphone",
          }),
        })
      );
    }
  }

  async unmuteMic() {
    logger.debug("unmuteMic()");

    if (!this._micProducer) {
      this.enableMic();
    } else {
      this._micProducer.resume();

      try {
        await this.sendRequest("resumeProducer", {
          producerId: this._micProducer.id,
        });

        store.dispatch(
          producerActions.setProducerResumed(this._micProducer.id)
        );
      } catch (error) {
        logger.error("unmuteMic() | failed: %o", error);

        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.microphoneUnMuteError",
              defaultMessage: "Unable to unmute your microphone",
            }),
          })
        );
      }
    }
  }

  changeMaxSpotlights(maxSpotlights) {
    this._spotlights.maxSpotlights = maxSpotlights;

    store.dispatch(settingsActions.setLastN(maxSpotlights));
  }

  // Updated consumers based on spotlights
  async updateSpotlights(spotlights) {
    logger.debug("updateSpotlights()");

    try {
      for (const consumer of this._consumers.values()) {
        if (consumer.kind === "video") {
          if (spotlights.indexOf(consumer.appData.peerId) > -1) {
            await this._resumeConsumer(consumer);
          } else {
            await this._pauseConsumer(consumer);
          }
        }
      }
    } catch (error) {
      logger.error("updateSpotlights() failed: %o", error);
    }
  }

  async changeAudioDevice(deviceId) {
    logger.debug("changeAudioDevice() [deviceId: %s]", deviceId);

    store.dispatch(meActions.setAudioInProgress(true));

    try {
      const device = this._audioDevices[deviceId];

      if (!device) throw new Error("no audio devices");

      logger.debug(
        "changeAudioDevice() | new selected webcam [device:%o]",
        device
      );

      if (this._micProducer && this._micProducer.track)
        this._micProducer.track.stop();

      logger.debug("changeAudioDevice() | calling getUserMedia()");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: device.deviceId },
          echoCancellation: true,
          googTypingNoiseDetection: true,
          googEchoCancellation: true,
          googAutoGainControl: true,
          googNoiseSuppression: true,
          googHighpassFilter: true,
          googAudioMirroring: false,
          googNoiseSuppression2: true,
          googEchoCancellation2: true,
          googAutoGainControl2: true,
          googDucking: true,
          chromeRenderToAssociatedSink: true,
          autoGainControl: true,
          channelCount: 1,
          noiseSuppression: true,
          sampleRate: 48000,
          sampleSize: 16,
        },
      });
      this._localStreams.push(stream);
      const track = stream.getAudioTracks()[0];
      this.appendRecording(stream);

      if (this._micProducer) await this._micProducer.replaceTrack({ track });

      if (this._micProducer) this._micProducer.volume = 0;

      const harkStream = new MediaStream();

      harkStream.addTrack(track);

      if (!harkStream.getAudioTracks()[0])
        throw new Error("changeAudioDevice(): given stream has no audio track");

      if (this._hark != null) this._hark.stop();

      this._hark = hark(harkStream.clone(), { play: false });

      // eslint-disable-next-line no-unused-vars
      this._hark.on("volume_change", (dBs, threshold) => {
        // The exact formula to convert from dBs (-100..0) to linear (0..1) is:
        //   Math.pow(10, dBs / 20)
        // However it does not produce a visually useful output, so let exagerate
        // it a bit. Also, let convert it from 0..1 to 0..10 and avoid value 1 to
        // minimize component renderings.
        let volume = Math.round(Math.pow(10, dBs / 85) * 10);

        if (volume === 1) volume = 0;

        volume = Math.round(volume);

        if (this._micProducer && volume !== this._micProducer.volume) {
          this._micProducer.volume = volume;

          store.dispatch(peerVolumeActions.setPeerVolume(this._peerId, volume));
        }
      });
      if (this._micProducer && this._micProducer.id)
        store.dispatch(
          producerActions.setProducerTrack(this._micProducer.id, track)
        );

      store.dispatch(settingsActions.setSelectedAudioDevice(deviceId));

      await this._updateAudioDevices();
    } catch (error) {
      logger.error("changeAudioDevice() failed: %o", error);
    }

    store.dispatch(meActions.setAudioInProgress(false));
  }

  async changeAudioOutputDevice(deviceId) {
    logger.debug('changeAudioOutputDevice() [deviceId:"%s"]', deviceId);

    store.dispatch(meActions.setAudioOutputInProgress(true));

    try {
      const device = this._audioOutputDevices[deviceId];

      if (!device)
        throw new Error("Selected audio output device no longer available");

      store.dispatch(settingsActions.setSelectedAudioOutputDevice(deviceId));

      await this._updateAudioOutputDevices();
    } catch (error) {
      logger.error('changeAudioOutputDevice() [error:"%o"]', error);
    }

    store.dispatch(meActions.setAudioOutputInProgress(false));
  }

  async changeVideoResolution(resolution) {
    logger.debug("changeVideoResolution() [resolution: %s]", resolution);

    store.dispatch(meActions.setWebcamInProgress(true));

    try {
      const deviceId = await this._getWebcamDeviceId();

      const device = this._webcams[deviceId];

      if (!device) throw new Error("no webcam devices");

      this._webcamProducer.track.stop();

      logger.debug("changeVideoResolution() | calling getUserMedia()");

      const conf = getConferenceConfig();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: device.deviceId },
          ...(VIDEO_CONSTRAINS[resolution] || VIDEO_CONSTRAINS.veryhigh),
          frameRate: conf.webcamFrameRate,
        },
      });
      this._localStreams.push(stream);

      const track = stream.getVideoTracks()[0];

      await this._webcamProducer.replaceTrack({ track });

      store.dispatch(
        producerActions.setProducerTrack(this._webcamProducer.id, track)
      );

      store.dispatch(settingsActions.setSelectedWebcamDevice(deviceId));
      store.dispatch(settingsActions.setVideoResolution(resolution));

      await this._updateWebcams();
    } catch (error) {
      logger.error("changeVideoResolution() failed: %o", error);
    }

    store.dispatch(meActions.setWebcamInProgress(false));
  }

  async changeWebcam(deviceId) {
    logger.debug("changeWebcam() [deviceId: %s]", deviceId);

    store.dispatch(meActions.setWebcamInProgress(true));

    try {
      const device = this._webcams[deviceId];
      const resolution = store.getState().settings.resolution;

      if (!device) throw new Error("no webcam devices");

      logger.debug("changeWebcam() | new selected webcam [device:%o]", device);
      if (this._webcamProducer && this._webcamProducer.track)
        this._webcamProducer.track.stop();

      logger.debug("changeWebcam() | calling getUserMedia()");

      const conf = getConferenceConfig();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: device.deviceId },
          ...(VIDEO_CONSTRAINS[resolution] || VIDEO_CONSTRAINS.veryhigh),
          frameRate: conf.webcamFrameRate,
        },
      });
      if (stream) {
        this._localStreams.push(stream);
        const track = stream.getVideoTracks()[0];
        if (track) {
          await this._webcamProducer.replaceTrack({ track });

          store.dispatch(
            producerActions.setProducerTrack(this._webcamProducer.id, track)
          );
        } else {
          logger.warn("getVideoTracks Error: First Video Track is null");
        }
      } else {
        logger.warn("getUserMedia Error: Stream is null!");
      }
      store.dispatch(settingsActions.setSelectedWebcamDevice(deviceId));

      await this._updateWebcams();
    } catch (error) {
      logger.error("changeWebcam() failed: %o", error);
    }

    store.dispatch(meActions.setWebcamInProgress(false));
  }

  setSelectedPeer(peerId) {
    logger.debug('setSelectedPeer() [peerId:"%s"]', peerId);

    this._spotlights.setPeerSpotlight(peerId);

    store.dispatch(roomActions.setSelectedPeer(peerId));
  }

  async promoteLobbyPeer(peerId) {
    logger.debug('promoteLobbyPeer() [peerId:"%s"]', peerId);

    store.dispatch(
      lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, true)
    );

    try {
      await this.sendRequest("promotePeer", { peerId });
    } catch (error) {
      logger.error("promoteLobbyPeer() failed: %o", error);
    }

    store.dispatch(
      lobbyPeerActions.setLobbyPeerPromotionInProgress(peerId, false)
    );
  }

  async kickPeer(peerId) {
    logger.debug('kickPeer() [peerId:"%s"]', peerId);

    store.dispatch(peerActions.setPeerKickInProgress(peerId, true));

    try {
      await this.sendRequest("moderator:kickPeer", { peerId });
    } catch (error) {
      logger.error("kickPeer() failed: %o", error);
    }

    store.dispatch(peerActions.setPeerKickInProgress(peerId, false));
  }

  async muteAllPeers() {
    logger.debug("muteAllPeers()");

    store.dispatch(roomActions.setMuteAllInProgress(true));

    try {
      await this.sendRequest("moderator:muteAll");
    } catch (error) {
      logger.error("muteAllPeers() failed: %o", error);
    }

    store.dispatch(roomActions.setMuteAllInProgress(false));
  }

  async stopAllPeerVideo() {
    logger.debug("stopAllPeerVideo()");

    store.dispatch(roomActions.setStopAllVideoInProgress(true));

    try {
      await this.sendRequest("moderator:stopAllVideo");
    } catch (error) {
      logger.error("stopAllPeerVideo() failed: %o", error);
    }

    store.dispatch(roomActions.setStopAllVideoInProgress(false));
  }

  async makeWebinar(flag) {
    logger.debug("makeWebinar()");

    store.dispatch(roomActions.setRoomWebinar(flag));

    if (flag) store.dispatch(roomActions.setDisplayMode("filmstrip"));
    else store.dispatch(roomActions.setDisplayMode("democratic"));

    try {
      await this.sendAddRole(this._peerId, "webinar_presenter");
      await this.sendRequest("moderator:makeWebinar", { flag });
    } catch (error) {
      logger.error("makeWebinar() failed: %o", error);
    }
  }

  async closeMeeting() {
    if (this._recording) this.stopRecording();

    logger.debug("closeMeeting()");

    store.dispatch(roomActions.setCloseMeetingInProgress(true));

    try {
      await this.sendRequest("moderator:closeMeeting");
    } catch (error) {
      logger.error("closeMeeting() failed: %o", error);
    }

    store.dispatch(roomActions.setCloseMeetingInProgress(false));
    // Kick bildirimi gelmese bile moderatör odayı terketsin
    this.close(true);
  }

  // type: mic/webcam/screen
  // mute: true/false
  async modifyPeerConsumer(peerId, type, mute) {
    logger.debug('modifyPeerConsumer() [peerId:"%s", type:"%s"]', peerId, type);

    let hasModerateRoomPermission = store
      .getState()
      .me.roles.some((role) =>
        store.getState().room.permissionsFromRoles.MODERATE_ROOM.includes(role)
      );

    if (hasModerateRoomPermission) {
      try {
        await this.sendRequest("moderator:modifyPeerConsumer", {
          peerId,
          type,
          mute,
        });
      } catch (error) {
        logger.error("modifyPeerConsumer() failed: %o", error);
      }
    } else {
      if (type === "mic")
        store.dispatch(peerActions.setPeerAudioInProgress(peerId, true));
      else if (type === "webcam")
        store.dispatch(peerActions.setPeerVideoInProgress(peerId, true));
      else if (type === "screen")
        store.dispatch(peerActions.setPeerScreenInProgress(peerId, true));

      try {
        for (const consumer of this._consumers.values()) {
          if (
            consumer.appData.peerId === peerId &&
            consumer.appData.source === type
          ) {
            if (mute) {
              await this._pauseConsumer(consumer);
            } else await this._resumeConsumer(consumer);
          }
        }
      } catch (error) {
        logger.error("modifyPeerConsumer() failed: %o", error);
      }

      if (type === "mic")
        store.dispatch(peerActions.setPeerAudioInProgress(peerId, false));
      else if (type === "webcam")
        store.dispatch(peerActions.setPeerVideoInProgress(peerId, false));
      else if (type === "screen")
        store.dispatch(peerActions.setPeerScreenInProgress(peerId, false));
    }
  }

  async _pauseConsumer(consumer) {
    logger.debug("_pauseConsumer() [consumer: %o]", consumer);

    if (consumer.paused || consumer.closed) return;

    try {
      await this.sendRequest("pauseConsumer", {
        consumerId: consumer.id,
      });

      consumer.pause();

      store.dispatch(consumerActions.setConsumerPaused(consumer.id, "local"));
    } catch (error) {
      logger.error("_pauseConsumer() | failed:%o", error);
    }
  }

  async _resumeConsumer(consumer) {
    logger.debug("_resumeConsumer() [consumer: %o]", consumer);

    if (!consumer.paused || consumer.closed) return;

    try {
      await this.sendRequest("resumeConsumer", {
        consumerId: consumer.id,
      });

      consumer.resume();

      store.dispatch(consumerActions.setConsumerResumed(consumer.id, "local"));
    } catch (error) {
      logger.error("_resumeConsumer() | failed:%o", error);
    }
  }

  async sendRaiseHandState(state) {
    logger.debug("sendRaiseHandState: ", state);

    store.dispatch(meActions.setMyRaiseHandStateInProgress(true));

    try {
      await this.sendRequest("raiseHand", {
        raiseHandState: state,
      });

      store.dispatch(meActions.setMyRaiseHandState(state));
    } catch (error) {
      logger.error("sendRaiseHandState() | failed: %o", error);

      // We need to refresh the component for it to render changed state
      store.dispatch(meActions.setMyRaiseHandState(!state));
    }

    store.dispatch(meActions.setMyRaiseHandStateInProgress(false));
  }

  async sendAddRole(peerId, role) {
    logger.debug("sendAddRole: ", role);

    try {
      await this.sendRequest("moderator:addRole", {
        peerId,
        role,
      });
    } catch (error) {
      logger.error("sendAddRole() | failed: %o", error);
    }
  }

  async sendRemoveRole(peerId, role) {
    logger.debug("sendRemoveRole: ", role);

    try {
      await this.sendRequest("moderator:removeRole", {
        peerId,
        role,
      });
    } catch (error) {
      logger.error("sendRemoveRole() | failed: %o", error);
    }
  }

  async setMaxSendingSpatialLayer(spatialLayer) {
    logger.debug("setMaxSendingSpatialLayer() [spatialLayer:%s]", spatialLayer);

    try {
      if (this._webcamProducer)
        await this._webcamProducer.setMaxSpatialLayer(spatialLayer);
      if (this._screenSharingProducer)
        await this._screenSharingProducer.setMaxSpatialLayer(spatialLayer);
    } catch (error) {
      logger.error('setMaxSendingSpatialLayer() | failed:"%o"', error);
    }
  }

  async setConsumerPreferredLayers(consumerId, spatialLayer, temporalLayer) {
    logger.debug(
      "setConsumerPreferredLayers() [consumerId:%s, spatialLayer:%s, temporalLayer:%s]",
      consumerId,
      spatialLayer,
      temporalLayer
    );

    try {
      await this.sendRequest("setConsumerPreferedLayers", {
        consumerId,
        spatialLayer,
        temporalLayer,
      });

      store.dispatch(
        consumerActions.setConsumerPreferredLayers(
          consumerId,
          spatialLayer,
          temporalLayer
        )
      );
    } catch (error) {
      logger.error('setConsumerPreferredLayers() | failed:"%o"', error);
    }
  }

  async restartIce() {
    logger.debug("restartIce()");

    try {
      if (this._sendTransport) {
        const iceParameters = await this.sendRequest("restartIce", {
          transportId: this._sendTransport.id,
        });

        await this._sendTransport.restartIce({ iceParameters });
      }

      if (this._recvTransport) {
        const iceParameters = await this.sendRequest("restartIce", {
          transportId: this._recvTransport.id,
        });

        await this._recvTransport.restartIce({ iceParameters });
      }

      logger.debug("ICE restarted");
    } catch (error) {
      logger.error("restartIce() | failed:%o", error);
    }
  }

  async setConsumerPriority(consumerId, priority) {
    logger.debug(
      "setConsumerPriority() [consumerId:%s, priority:%d]",
      consumerId,
      priority
    );

    try {
      await this.sendRequest("setConsumerPriority", {
        consumerId,
        priority,
      });

      store.dispatch(consumerActions.setConsumerPriority(consumerId, priority));
    } catch (error) {
      logger.error("setConsumerPriority() | failed:%o", error);
    }
  }

  async requestConsumerKeyFrame(consumerId) {
    logger.debug("requestConsumerKeyFrame() [consumerId:%s]", consumerId);

    try {
      await this.sendRequest("requestConsumerKeyFrame", {
        consumerId,
      });
    } catch (error) {
      logger.error("requestConsumerKeyFrame() | failed:%o", error);
    }
  }

  async _loadDynamicImports() {
    ({ default: saveAs } = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "file-saver" */
      "file-saver"
    ));

    ({ default: ScreenShare } = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "screensharing" */
      "./ScreenShare"
    ));

    ({ default: Spotlights } = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "spotlights" */
      "./Spotlights"
    ));

    mediasoupClient = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "mediasoup" */
      "mediasoup-client"
    );

    ({ default: io } = await import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "socket.io" */
      "socket.io-client"
    ));
  }

  async join({ roomId, roomName, joinVideo, joinAudio, call, joinScreen }) {
    if (this._roomJoining === true || this._roomJoined === true) return;
    this._closed = false;
    this._roomJoining = true;
    this._roomJoined = true;
    await this._loadDynamicImports();

    this._roomId = roomId;
    this._call = call;
    this._joinScreen = Boolean(joinScreen);
    if (this._joinScreen) {
      // Ekran paylaşımlı aramada mic/kamera açılmasın.
      this._muted = true;
      joinAudio = false;
      joinVideo = false;
    }

    store.dispatch(roomActions.setRoomName(roomName));

    this._signalingUrl = getSignalingUrl(
      this._peerId,
      roomName,
      roomId,
      localStorage.getItem("token")
    );

    this._screenSharing = ScreenShare.create(this._device);

    this._screenSharingForRecorder = ScreenShare.create(this._device);

    this._signalingSocket = io(this._signalingUrl, {
      transports: ["websocket"],
      pingInterval: 60000,
      pingTimeout: 60000,
      timeout: 60000,
    });

    this._spotlights = new Spotlights(
      this._maxSpotlights,
      this._signalingSocket
    );

    store.dispatch(roomActions.setRoomState("connecting"));

    this._signalingSocket.on("connect", () => {
      logger.debug('signaling Peer "connect" event');
      this._signalingSocket.on("transcript", (transcriptFile) => {
        let data = transcriptGenerator(transcriptFile);
        let joined = data.join();
        let newFile = joined.replace(/,/g, " ");
        //transcriptGenerator(transcriptFile);
        let file = new Blob([newFile], { type: "text/plain;charset=utf-8" });
        saveAs(file, "transcript.txt");
      });
      this._roomJoining = false;
    });

    this._signalingSocket.on("disconnect", (reason) => {
      logger.warn('signaling Peer "disconnect" event [reason:"%s"]', reason);

      this._signalingSocket.off("transcript");
      if (this._closed) return;

      if (reason === "io server disconnect") {
        store.dispatch(
          requestActions.notify({
            text: intl.formatMessage({
              id: "socket.disconnected",
              defaultMessage: "You are disconnected",
            }),
          })
        );

        this.close();
      }

      if (!this._call)
        store.dispatch(
          requestActions.notify({
            text: intl.formatMessage({
              id: "socket.reconnecting",
              defaultMessage: "You are disconnected, attempting to reconnect",
            }),
          })
        );

      if (this._screenSharingProducer) {
        this._stopProducerTrack(this._screenSharingProducer);
        this._screenSharingProducer.close();

        store.dispatch(
          producerActions.removeProducer(this._screenSharingProducer.id)
        );

        this._screenSharingProducer = null;
      }

      if (this._webcamProducer) {
        this._stopProducerTrack(this._webcamProducer);
        this._webcamProducer.close();

        store.dispatch(producerActions.removeProducer(this._webcamProducer.id));

        this._webcamProducer = null;
      }

      if (this._micProducer) {
        this._stopProducerTrack(this._micProducer);
        this._micProducer.close();

        store.dispatch(producerActions.removeProducer(this._micProducer.id));

        this._micProducer = null;
      }

      if (this._sendTransport) {
        this._closeRemoteControlDataProducers();
        this._sendTransport.close();

        this._sendTransport = null;
        this._sendTransportHasSctp = false;
      }

      if (this._recvTransport) {
        this._recvTransport.close();

        this._recvTransport = null;
      }

      store.dispatch(roomActions.setRoomState("connecting"));
    });

    this._signalingSocket.on("reconnect_failed", () => {
      logger.warn('signaling Peer "reconnect_failed" event');

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage({
            id: "socket.disconnected",
            defaultMessage: "You are disconnected",
          }),
        })
      );

      this.close();
    });

    this._signalingSocket.on("reconnect", (attemptNumber) => {
      logger.debug(
        'signaling Peer "reconnect" event [attempts:"%s"]',
        attemptNumber
      );

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage({
            id: "socket.reconnected",
            defaultMessage: "You are reconnected",
          }),
        })
      );

      store.dispatch(roomActions.setRoomState("connected"));
    });

    this._signalingSocket.on("request", async (request, cb) => {
      logger.debug(
        'socket "request" event [method:%s, data:%o]',
        request.method,
        request.data
      );

      switch (request.method) {
        case "newConsumer": {
          const {
            peerId,
            producerId,
            id,
            kind,
            rtpParameters,
            type,
            appData,
            producerPaused,
          } = request.data;

          let codecOptions;

          if (kind === "audio") {
            codecOptions = {
              opusStereo: false,
              opusDtx: true,
              opusFec: true,
              opusMaxPlaybackRate: 48000,
            };
          }

          const consumer = await this._recvTransport.consume({
            id,
            producerId,
            kind,
            rtpParameters,
            codecOptions,
            appData: { ...appData, peerId }, // Trick.
          });

          // Store in the map.
          this._consumers.set(consumer.id, consumer);

          consumer.on("transportclose", () => {
            this._consumers.delete(consumer.id);
          });

          const {
            spatialLayers,
            temporalLayers,
          } = mediasoupClient.parseScalabilityMode(
            consumer.rtpParameters.encodings[0].scalabilityMode
          );

          store.dispatch(
            consumerActions.addConsumer(
              {
                id: consumer.id,
                peerId: peerId,
                kind: kind,
                type: type,
                locallyPaused: false,
                remotelyPaused: producerPaused,
                rtpParameters: consumer.rtpParameters,
                source: consumer.appData.source,
                spatialLayers: spatialLayers,
                temporalLayers: temporalLayers,
                preferredSpatialLayer: spatialLayers - 1,
                preferredTemporalLayer: temporalLayers - 1,
                priority: 1,
                codec: consumer.rtpParameters.codecs[0].mimeType.split("/")[1],
                track: consumer.track,
              },
              peerId
            )
          );

          // We are ready. Answer the request so the server will
          // resume this Consumer (which was paused for now).
          cb(null);

          if (kind === "audio") {
            const rstream = new MediaStream();
            rstream.addTrack(consumer.track);
            this.appendRecording(rstream);

            const stream = new MediaStream();

            stream.addTrack(consumer.track);

            if (!stream.getAudioTracks()[0])
              throw new Error(
                "request.newConsumer | given stream has no audio track"
              );
          }

          break;
        }

        case "newDataConsumer": {
          const {
            peerId,
            dataProducerId,
            id,
            sctpStreamParameters,
            label,
            protocol,
            appData,
          } = request.data;

          const dataConsumer = await this._recvTransport.consumeData({
            id,
            dataProducerId,
            sctpStreamParameters,
            label,
            protocol,
            appData: { ...(appData || {}), peerId },
          });

          this._dataConsumers.set(dataConsumer.id, dataConsumer);

          dataConsumer.on("transportclose", () => {
            this._dataConsumers.delete(dataConsumer.id);
          });

          dataConsumer.on("close", () => {
            this._dataConsumers.delete(dataConsumer.id);
          });

          if (isRemoteControlLabel(label)) {
            rcLog("newDataConsumer", { label, peerId, id });
            dataConsumer.on("message", (data) => {
              this._onRemoteControlData(data);
            });
          }

          cb(null);
          break;
        }

        default: {
          logger.error('unknown request.method "%s"', request.method);

          cb(500, `unknown request.method "${request.method}"`);
        }
      }
    });

    this._signalingSocket.on("notification", async (notification) => {
      logger.debug(
        'socket "notification" event [method:%s, data:%o]',
        notification.method,
        notification.data
      );

      try {
        switch (notification.method) {
          case "enteredLobby": {
            store.dispatch(roomActions.setInLobby(true));

            const { displayName } = store.getState().settings;
            const { picture } = store.getState().me;

            await this.sendRequest("changeDisplayName", {
              displayName,
            });
            await this.sendRequest("changePicture", {
              picture,
            });
            break;
          }

          case "signInRequired": {
            store.dispatch(roomActions.setSignInRequired(true));

            break;
          }

          case "roomReady": {
            if (store.getState().room.joined === false)
              store.dispatch(roomActions.toggleJoined());
            store.dispatch(roomActions.setInLobby(false));

            await this._joinRoom({
              joinVideo,
              joinAudio,
              joinScreen: this._joinScreen,
            });

            break;
          }

          case "roomBack": {
            await this._joinRoom({
              joinVideo,
              joinAudio,
              joinScreen: this._joinScreen,
            });

            break;
          }

          case "raiseHand": {
            const { peerId, raisedHand } = notification.data;

            store.dispatch(
              peerActions.setPeerRaiseHandState(peerId, raisedHand)
            );

            break;
          }

          case "lockRoom": {
            store.dispatch(roomActions.setRoomLocked());

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.locked",
                  defaultMessage: "Room is now locked",
                }),
              })
            );

            break;
          }

          case "unlockRoom": {
            store.dispatch(roomActions.setRoomUnLocked());

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.unlocked",
                  defaultMessage: "Room is now unlocked",
                }),
              })
            );

            break;
          }

          case "parkedPeer": {
            const { id, displayName, picture } = notification.data;

            store.dispatch(lobbyPeerActions.addLobbyPeer(id));
            store.dispatch(
              lobbyPeerActions.setLobbyPeerDisplayName(displayName, id)
            );
            store.dispatch(lobbyPeerActions.setLobbyPeerPicture(picture));

            //store.dispatch(roomActions.setToolbarsVisible(true)); // Works with AppBar. There is none in Wunity

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.newLobbyPeer",
                  defaultMessage: "New participant entered the lobby",
                }),
              })
            );

            break;
          }

          case "lobby:peerClosed": {
            const { peerId } = notification.data;

            store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.lobbyPeerLeft",
                  defaultMessage: "Participant in lobby left",
                }),
              })
            );

            break;
          }

          case "lobby:promotedPeer": {
            const { peerId } = notification.data;

            store.dispatch(lobbyPeerActions.removeLobbyPeer(peerId));

            break;
          }

          case "lobby:changeDisplayName": {
            const { peerId, displayName } = notification.data;

            store.dispatch(
              lobbyPeerActions.setLobbyPeerDisplayName(displayName, peerId)
            );

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage(
                  {
                    id: "room.lobbyPeerChangedDisplayName",
                    defaultMessage:
                      "Participant in lobby changed name to {displayName}",
                  },
                  {
                    displayName,
                  }
                ),
              })
            );

            break;
          }

          case "lobby:changePicture": {
            const { peerId, picture } = notification.data;

            store.dispatch(
              lobbyPeerActions.setLobbyPeerPicture(picture, peerId)
            );

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.lobbyPeerChangedPicture",
                  defaultMessage: "Participant in lobby changed picture",
                }),
              })
            );

            break;
          }

          case "setAccessCode": {
            const { accessCode } = notification.data;

            store.dispatch(roomActions.setAccessCode(accessCode));

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "room.setAccessCode",
                  defaultMessage: "Access code for room updated",
                }),
              })
            );

            break;
          }

          case "setJoinByAccessCode": {
            const { joinByAccessCode } = notification.data;

            store.dispatch(roomActions.setJoinByAccessCode(joinByAccessCode));

            if (joinByAccessCode) {
              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "room.accessCodeOn",
                    defaultMessage: "Access code for room is now activated",
                  }),
                })
              );
            } else {
              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "room.accessCodeOff",
                    defaultMessage: "Access code for room is now deactivated",
                  }),
                })
              );
            }

            break;
          }
          case "changeDisplayName": {
            const { peerId, displayName, oldDisplayName } = notification.data;

            store.dispatch(peerActions.setPeerDisplayName(displayName, peerId));

            /*            store.dispatch(
              chatActions.addJoinedMessage({
                type: "USER_ACTIVITY",
                text: oldDisplayName + " changed name to " + displayName,
                time: new Date().toString(),
                name: displayName,
                sender: "SYSTEM",
              })
            );*/

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage(
                  {
                    id: "room.peerChangedDisplayName",
                    defaultMessage: "{oldDisplayName} is now {displayName}",
                  },
                  {
                    oldDisplayName,
                    displayName,
                  }
                ),
              })
            );

            break;
          }

          case "changePicture": {
            const { peerId, picture } = notification.data;

            store.dispatch(peerActions.setPeerPicture(peerId, picture));

            break;
          }

          case "chatMessage": {
            const { peerId, chatMessage } = notification.data;

            store.dispatch(
              chatActions.addResponseMessage({
                ...chatMessage,
                peerId,
              })
            );

            if (
              !store.getState().toolarea.toolAreaOpen ||
              (store.getState().toolarea.toolAreaOpen &&
                store.getState().toolarea.currentToolTab !== "chat")
            ) {
              //store.dispatch(roomActions.setToolbarsVisible(true)); // Works with AppBar. There is none in Wunity
              // Make sound
              this._soundNotification();
            }

            break;
          }

          case "meetingDetails": {
            const { peerId, meetingDetails } = notification.data;

            store.dispatch(meetingManagementActions.setMeeting(meetingDetails));
            store.dispatch(roomActions.setOfficialReportOpenGlobal(true));
            break;
          }
          case "privateChatMessage": {
            const { peerId, chatMessage } = notification.data;

            store.dispatch(
              privateChatActions.addPrivateResponseMessage({
                ...chatMessage,
                peerId,
                // selectedPeerId
              })
            );

            if (
              !store.getState().toolarea.toolAreaOpen ||
              (store.getState().toolarea.toolAreaOpen &&
                store.getState().toolarea.currentToolTab !== "privateChat")
            ) {
              //store.dispatch(roomActions.setToolbarsVisible(true)); // Works with AppBar. There is none in Wunity
              // Make sound
              this._soundNotification();
            }

            break;
          }

          case "whiteBoardEvent": {
            const { event } = notification.data;
            store.dispatch(whiteBoardActions.setData(event.data));

            if (!store.getState().whiteboard.whiteBoardOpen) {
              //store.dispatch(roomActions.setToolbarsVisible(true)); // Works with AppBar. There is none in Wunity
              this._soundNotification();
            }
            break;
          }

          case "publishSurvey": {
            const survey = notification.data;
            store.dispatch(surveyActions.setCurrentSurvey(survey));
            this._soundNotification();
            break;
          }

          case "publishSurveyAnswer": {
            const answer = notification.data;
            store.dispatch(surveyActions.addAnswer(answer));
            this._soundNotification();
            break;
          }

          case "producerScore": {
            const { producerId, score } = notification.data;

            store.dispatch(producerActions.setProducerScore(producerId, score));

            break;
          }

          case "newPeer": {
            const { id, displayName, picture, roles, clientInfo } = notification.data;

            store.dispatch(
              peerActions.addPeer({
                id,
                displayName,
                picture,
                roles,
                clientInfo,
                consumers: [],
              })
            );

            /*store.dispatch(
              chatActions.addJoinedMessage({
                type: "USER_ACTIVITY",
                text: displayName + " joined the meeting",
                time: new Date().toString(),
                name: displayName,
                sender: "SYSTEM",
              })
            );*/
            let peersLength = Object.keys(store.getState().peers).length;
            let webinar = store.getState().room.webinar;

            if (!webinar && peersLength < 30 && !this._call) {
              {
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage(
                      {
                        id: "room.newPeer",
                        defaultMessage: "{displayName} joined the room",
                      },
                      {
                        displayName,
                      }
                    ),
                  })
                );
              }
            }

            break;
          }

          case "peerClosed": {
            const { peerId } = notification.data;
            const peer = store.getState().peers[peerId];
            let peersLength = Object.keys(store.getState().peers).length;
            if (peer && peer.displayName && peersLength < 30) {
              /*              store.dispatch(
                chatActions.addJoinedMessage({
                  type: "USER_ACTIVITY",
                  text: peer.displayName + " left the meeting",
                  time: new Date().toString(),
                  name: peer.displayName,
                  sender: "SYSTEM",
                })
              );*/
            }

            store.dispatch(peerActions.removePeer(peerId));

            break;
          }

          case "consumerClosed": {
            const { consumerId } = notification.data;
            const consumer = this._consumers.get(consumerId);

            if (!consumer) break;

            consumer.close();

            if (consumer.hark != null) consumer.hark.stop();

            this._consumers.delete(consumerId);

            const { peerId } = consumer.appData;
            if (store.getState().room.fullScreenConsumer === consumerId)
              store.dispatch(roomActions.toggleConsumerFullscreen(consumerId));

            store.dispatch(consumerActions.removeConsumer(consumerId, peerId));

            break;
          }

          case "consumerPaused": {
            const { consumerId } = notification.data;
            const consumer = this._consumers.get(consumerId);

            if (!consumer) break;

            store.dispatch(
              consumerActions.setConsumerPaused(consumerId, "remote")
            );

            break;
          }

          case "consumerResumed": {
            const { consumerId } = notification.data;
            const consumer = this._consumers.get(consumerId);

            if (!consumer) break;

            store.dispatch(
              consumerActions.setConsumerResumed(consumerId, "remote")
            );

            break;
          }

          case "consumerLayersChanged": {
            const {
              consumerId,
              spatialLayer,
              temporalLayer,
            } = notification.data;
            const consumer = this._consumers.get(consumerId);

            if (!consumer) break;

            store.dispatch(
              consumerActions.setConsumerCurrentLayers(
                consumerId,
                spatialLayer,
                temporalLayer
              )
            );

            break;
          }

          case "consumerScore": {
            const { consumerId, score } = notification.data;

            store.dispatch(consumerActions.setConsumerScore(consumerId, score));

            break;
          }

          case "moderator:mute": {
            // const { peerId } = notification.data;

            if (this._micProducer && !this._micProducer.paused) {
              this.muteMic();

              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "moderator.mute",
                    defaultMessage: "Moderator muted your microphone",
                  }),
                })
              );
            }

            break;
          }

          case "moderator:stopVideo": {
            // const { peerId } = notification.data;

            this.disableWebcam();
            this.disableScreenSharing();

            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "moderator.stopVideo",
                  defaultMessage: "Moderator stopped your video",
                }),
              })
            );

            break;
          }

          case "moderator:room:webinar": {
            const { flag } = notification.data;

            if (flag) {
              if (this._micProducer && !this._micProducer.paused)
                this.muteMic();

              this.disableWebcam();
              this.disableScreenSharing();

              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "moderator.hasMakeWebinarThisRoom",
                    defaultMessage: "Moderator has make webinar this room",
                  }),
                })
              );
            } else {
              store.dispatch(
                requestActions.notify({
                  text: intl.formatMessage({
                    id: "moderator.cancelledWebinarForThisRoom",
                    defaultMessage: "Moderator cancelled webinar for this room",
                  }),
                })
              );
            }

            store.dispatch(roomActions.setRoomWebinar(flag));

            break;
          }

          case "moderator:kick": {
            // Need some feedback
            this.close();

            break;
          }

          case "gotRole": {
            const { peerId, role } = notification.data;
            if (peerId === this._peerId) {
              store.dispatch(meActions.addRole(role));

              if (role === "webinar_speaker") {
                this.sendRaiseHandState(false);
                store.dispatch(meActions.setMyRaiseHandState(false));
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "roles.webinarSpeaker",
                      defaultMessage: `You got the webinar speaker role`,
                    }),
                  })
                );
              } else if (role === "webinar_presenter") {
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "roles.roomIsNowWebinar",
                      defaultMessage: `Room is now Webinar`,
                    }),
                  })
                );
              } else
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "roles.gotRole",
                      defaultMessage: `You got the role: ${role}`,
                    }),
                  })
                );
            } else store.dispatch(peerActions.addPeerRole(peerId, role));

            break;
          }

          case "lostRole": {
            const { peerId, role } = notification.data;

            if (peerId === this._peerId) {
              store.dispatch(meActions.removeRole(role));

              if (role === "webinar_speaker") {
                if (this._micProducer && !this._micProducer.paused)
                  this.muteMic();

                this.disableWebcam();
                this.disableScreenSharing();
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "roles.cancelledWebinarSpeaker",
                      defaultMessage: `You got the webinar speaker role`,
                    }),
                  })
                );
              } else
                store.dispatch(
                  requestActions.notify({
                    text: intl.formatMessage({
                      id: "roles.lostRole",
                      defaultMessage: `You lost the role: ${role}`,
                    }),
                  })
                );
            } else store.dispatch(peerActions.removePeerRole(peerId, role));

            break;
          }

          case "setRecordingNotificationStatus": {
            const { recordingNotificationStatus } = notification.data;
            store.dispatch(
              roomActions.setRecordingNotificationStatus(
                recordingNotificationStatus
              )
            );
            break;
          }

          case "setLiveStreamingNotificationStatus": {
            const { liveStreamingNotificationStatus } = notification.data;
            store.dispatch(
              roomActions.setLiveStreamingNotificationStatus(
                liveStreamingNotificationStatus
              )
            );
            break;
          }

          case "startRecording": {
            const { recorderId } = notification.data;
            this.startRemoteRecording();
            break;
          }

          case "remoteControl:state": {
            const { session, reason } = notification.data || {};

            this._applyRemoteControlSession(session, reason);
            break;
          }

          case "remoteControl:incoming": {
            const current = store.getState().remoteControl;

            if (!current.sessionId && notification.data) {
              this._applyRemoteControlSession({
                ...notification.data,
                status: "requested",
                hostPeerId: store.getState().me.id,
              });
            }

            break;
          }

          case "dataConsumerClosed": {
            const { dataConsumerId } = notification.data || {};
            const dataConsumer = this._dataConsumers.get(dataConsumerId);

            if (dataConsumer) {
              dataConsumer.close();
              this._dataConsumers.delete(dataConsumerId);
            }

            break;
          }

          default: {
            logger.error(
              'unknown notification.method "%s"',
              notification.method
            );
          }
        }
      } catch (error) {
        logger.error('error on socket "notification" event failed:"%o"', error);

        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "socket.requestError",
              defaultMessage: "Error on server request",
            }),
          })
        );
      }
    });
  }

  async _joinRoom({ joinVideo, joinAudio, joinScreen }) {
    logger.debug("_joinRoom()");
    this._joinScreen = Boolean(joinScreen || this._joinScreen);

    const { displayName } = store.getState().settings;
    const { picture } = store.getState().me;

    try {
      this._mediasoupDevice = new mediasoupClient.Device();

      const routerRtpCapabilities = await this.sendRequest(
        "getRouterRtpCapabilities"
      );

      await this._mediasoupDevice.load({
        routerRtpCapabilities,
      });

      if (this._produce) {
        const transportInfo = await this.sendRequest("createWebRtcTransport", {
          forceTcp: this._forceTcp,
          producing: true,
          consuming: false,
          enableSctp: true,
        });

        const {
          id,
          iceParameters,
          iceCandidates,
          dtlsParameters,
          sctpParameters,
        } = transportInfo;

        this._sendTransportHasSctp = Boolean(sctpParameters);

        this._sendTransport = this._mediasoupDevice.createSendTransport({
          id,
          iceParameters,
          iceCandidates,
          dtlsParameters,
          sctpParameters,
          iceServers: ROOM_OPTIONS.turnServers,
          proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
        });

        this._sendTransport.on(
          "connect",
          (
            { dtlsParameters },
            callback,
            errback // eslint-disable-line no-shadow
          ) => {
            this.sendRequest("connectWebRtcTransport", {
              transportId: this._sendTransport.id,
              dtlsParameters,
            })
              .then(callback)
              .catch(errback);
          }
        );

        this._sendTransport.on(
          "produce",
          async ({ kind, rtpParameters, appData }, callback, errback) => {
            try {
              // eslint-disable-next-line no-shadow
              const { id } = await this.sendRequest("produce", {
                transportId: this._sendTransport.id,
                kind,
                rtpParameters,
                appData,
              });

              callback({ id });
            } catch (error) {
              errback(error);
            }
          }
        );

        this._sendTransport.on(
          "producedata",
          async (
            { sctpStreamParameters, label, protocol, appData },
            callback,
            errback
          ) => {
            try {
              const { id } = await this.sendRequest("produceData", {
                transportId: this._sendTransport.id,
                sctpStreamParameters,
                label,
                protocol,
                appData,
              });

              callback({ id });
            } catch (error) {
              errback(error);
            }
          }
        );
      }

      if (this._sendTransport) {
        this._sendTransport.on("connectionstatechange", (connectState) => {
          switch (connectState) {
            case "disconnected":
            case "failed":
              this.restartIce();
              break;

            default:
              break;
          }
        });
      }

      const transportInfo = await this.sendRequest("createWebRtcTransport", {
        forceTcp: this._forceTcp,
        producing: false,
        consuming: true,
        enableSctp: true,
      });

      const {
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
        sctpParameters,
      } = transportInfo;

      this._recvTransport = this._mediasoupDevice.createRecvTransport({
        id,
        iceParameters,
        iceCandidates,
        dtlsParameters,
        sctpParameters,
        iceServers: ROOM_OPTIONS.turnServers,
      });

      this._recvTransport.on(
        "connect",
        (
          { dtlsParameters },
          callback,
          errback // eslint-disable-line no-shadow
        ) => {
          this.sendRequest("connectWebRtcTransport", {
            transportId: this._recvTransport.id,
            dtlsParameters,
          })
            .then(callback)
            .catch(errback);
        }
      );

      this._recvTransport.on("connectionstatechange", (connectState) => {
        switch (connectState) {
          case "disconnected":
          case "failed":
            this.restartIce();
            break;

          default:
            break;
        }
      });

      // Set our media capabilities.
      store.dispatch(
        meActions.setMediaCapabilities({
          canSendMic: this._mediasoupDevice.canProduce("audio"),
          canSendWebcam: this._mediasoupDevice.canProduce("video"),
          canShareScreen:
            this._mediasoupDevice.canProduce("video") &&
            this._screenSharing.isScreenShareAvailable(),
          canShareFiles: false,
        })
      );

      const {
        roles,
        peers,
        authenticated,
        permissionsFromRoles,
        userRoles,
        remoteControl,
      } = await this.sendRequest("join", {
        displayName: displayName,
        picture: picture,
        rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
        clientInfo: getClientInfo(),
      });

      store.dispatch(meActions.loggedIn(authenticated));
      store.dispatch(meActions.setDesktopCapabilities(getClientInfo()));
      this._applyRemoteControlSession(remoteControl);

      store.dispatch(roomActions.setUserRoles(userRoles));
      store.dispatch(roomActions.setPermissionsFromRoles(permissionsFromRoles));

      const myRoles = store.getState().me.roles;

      for (const role of roles) {
        if (!myRoles.includes(role)) {
          store.dispatch(meActions.addRole(role));
          if (role !== "webinar_presenter" && !this._call)
            store.dispatch(
              requestActions.notify({
                text: intl.formatMessage({
                  id: "roles.gotRole",
                  defaultMessage: `You got the role: ${role}`,
                }),
              })
            );
        }
      }

      for (const peer of peers) {
        store.dispatch(peerActions.addPeer({ ...peer, consumers: [] }));
      }

      this._spotlights.addPeers(peers);

      this._spotlights.on("spotlights-updated", (spotlights) => {
        store.dispatch(roomActions.setSpotlights(spotlights));
        this.updateSpotlights(spotlights);
      });

      await this._updateAudioOutputDevices();

      if (this._audioOutputDevices !== {}) {
        let defaultDevice = this._audioOutputDevices.default
          ? this._audioOutputDevices.default.deviceId
          : Object.keys(this._audioOutputDevices)[0];
        store.dispatch(
          settingsActions.setSelectedAudioOutputDevice(defaultDevice)
        );
      }

      store.dispatch(roomActions.setRoomState("connected"));

      // Clean all the existing notifcations.
      store.dispatch(notificationActions.removeAllNotifications());

      // Arama odasında chat paneli varsayılan olarak açık gelsin.
      if (this._call) {
        store.dispatch(toolAreaActions.setToolTab("chat"));
        store.dispatch(toolAreaActions.openToolArea());
      }

      this.getServerHistory(joinAudio, joinVideo, this._joinScreen);
      if (!this._call)
        store.dispatch(
          requestActions.notify({
            text: intl.formatMessage({
              id: "room.joined",
              defaultMessage: "You have joined the room",
            }),
          })
        );

      /*      store.dispatch(
        chatActions.addJoinedMessage({
          type: "ROOM_ACTIVITY",
          text: "Meeting Started",
          time: new Date().toString(),
          name: displayName,
          sender: "SYSTEM",
        })
      );*/

      /*      store.dispatch(
        chatActions.addJoinedMessage({
          type: "USER_ACTIVITY",
          text: displayName + " (You) joined the meeting",
          time: new Date().toString(),
          name: displayName,
          sender: "SYSTEM",
        })
      );*/

      this._spotlights.start();
    } catch (error) {
      logger.error('_joinRoom() failed:"%o"', error);

      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.cantJoin",
            defaultMessage: "Unable to join the room",
          }),
        })
      );

      this.close();
    }
  }

  async lockRoom() {
    logger.debug("lockRoom()");

    try {
      await this.sendRequest("lockRoom");

      store.dispatch(roomActions.setRoomLocked());

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage({
            id: "room.youLocked",
            defaultMessage: "You locked the room",
          }),
        })
      );
    } catch (error) {
      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.cantLock",
            defaultMessage: "Unable to lock the room",
          }),
        })
      );

      logger.error("lockRoom() | failed: %o", error);
    }
  }

  async unlockRoom() {
    logger.debug("unlockRoom()");

    try {
      await this.sendRequest("unlockRoom");

      store.dispatch(roomActions.setRoomUnLocked());

      store.dispatch(
        requestActions.notify({
          text: intl.formatMessage({
            id: "room.youUnLocked",
            defaultMessage: "You unlocked the room",
          }),
        })
      );
    } catch (error) {
      store.dispatch(
        requestActions.notify({
          type: "error",
          text: intl.formatMessage({
            id: "room.cantUnLock",
            defaultMessage: "Unable to unlock the room",
          }),
        })
      );

      logger.error("unlockRoom() | failed: %o", error);
    }
  }

  async setAccessCode(code) {
    logger.debug("setAccessCode()");

    try {
      await this.sendRequest("setAccessCode", {
        accessCode: code,
      });

      store.dispatch(roomActions.setAccessCode(code));

      store.dispatch(
        requestActions.notify({
          text: "Access code saved.",
        })
      );
    } catch (error) {
      logger.error("setAccessCode() | failed: %o", error);
      store.dispatch(
        requestActions.notify({
          type: "error",
          text: "Unable to set access code.",
        })
      );
    }
  }

  async setJoinByAccessCode(value) {
    logger.debug("setJoinByAccessCode()");

    try {
      await this.sendRequest("setJoinByAccessCode", {
        joinByAccessCode: value,
      });

      store.dispatch(roomActions.setJoinByAccessCode(value));

      store.dispatch(
        requestActions.notify({
          text: `You switched Join by access-code to ${value}`,
        })
      );
    } catch (error) {
      logger.error("setAccessCode() | failed: %o", error);
      store.dispatch(
        requestActions.notify({
          type: "error",
          text: "Unable to set join by access code.",
        })
      );
    }
  }

  async enableMic() {
    if (this._micProducer) return;

    if (this._mediasoupDevice && !this._mediasoupDevice.canProduce("audio")) {
      logger.error("enableMic() | cannot produce audio");

      return;
    }

    let track;

    store.dispatch(meActions.setAudioInProgress(true));

    try {
      const deviceId = await this._getAudioDeviceId();

      const device = this._audioDevices[deviceId];

      if (!device) throw new Error("no audio devices");

      logger.debug(
        "enableMic() | new selected audio device [device:%o]",
        device
      );

      logger.debug("enableMic() | calling getUserMedia()");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { ideal: deviceId },
          echoCancellation: true,
          googTypingNoiseDetection: true,
          googEchoCancellation: true,
          googAutoGainControl: true,
          googNoiseSuppression: true,
          googHighpassFilter: true,
          googAudioMirroring: false,
          googNoiseSuppression2: true,
          googEchoCancellation2: true,
          googAutoGainControl2: true,
          googDucking: true,
          chromeRenderToAssociatedSink: true,
          autoGainControl: true,
          channelCount: 1,
          noiseSuppression: true,
          sampleRate: 48000,
          sampleSize: 16,
        },
      });
      this._localStreams.push(stream);

      this.appendRecording(stream);
      track = stream.getAudioTracks()[0];

      this._micProducer = await this._sendTransport.produce({
        track,
        codecOptions: {
          opusStereo: false,
          opusDtx: true,
          opusFec: true,
          opusMaxPlaybackRate: 48000,
        },
        appData: { source: "mic" },
      });

      store.dispatch(
        producerActions.addProducer({
          id: this._micProducer.id,
          source: "mic",
          paused: this._micProducer.paused,
          track: this._micProducer.track,
          rtpParameters: this._micProducer.rtpParameters,
          codec: this._micProducer.rtpParameters.codecs[0].mimeType.split(
            "/"
          )[1],
        })
      );

      store.dispatch(settingsActions.setSelectedAudioDevice(deviceId));

      await this._updateAudioDevices();

      this._micProducer.on("transportclose", () => {
        this._micProducer = null;
      });

      this._micProducer.on("trackended", () => {
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.microphoneDisconnected",
              defaultMessage: "Microphone disconnected",
            }),
          })
        );

        this.disableMic().catch(() => {});
      });

      this._micProducer.volume = 0;

      const harkStream = new MediaStream();

      harkStream.addTrack(track);

      if (!harkStream.getAudioTracks()[0])
        throw new Error("enableMic(): given stream has no audio track");

      if (this._hark != null) this._hark.stop();

      this._hark = hark(harkStream.clone(), { play: false });

      // eslint-disable-next-line no-unused-vars
      this._hark.on("volume_change", (dBs, threshold) => {
        // The exact formula to convert from dBs (-100..0) to linear (0..1) is:
        //   Math.pow(10, dBs / 20)
        // However it does not produce a visually useful output, so let exagerate
        // it a bit. Also, let convert it from 0..1 to 0..10 and avoid value 1 to
        // minimize component renderings.
        let volume = Math.round(Math.pow(10, dBs / 85) * 10);

        if (volume === 1) volume = 0;

        volume = Math.round(volume);

        if (this._micProducer && volume !== this._micProducer.volume) {
          this._micProducer.volume = volume;

          store.dispatch(peerVolumeActions.setPeerVolume(this._peerId, volume));
        }
      });
    } catch (error) {
      logger.error("enableMic() failed:%o", error);
      if (!this._call)
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.microphoneError",
              defaultMessage:
                "An error occured while accessing your microphone",
            }),
          })
        );

      if (track) track.stop();
    }

    store.dispatch(meActions.setAudioInProgress(false));
  }

  async disableMic() {
    logger.debug("disableMic()");

    if (!this._micProducer) return;

    store.dispatch(meActions.setAudioInProgress(true));

    this._stopProducerTrack(this._micProducer);
    this._micProducer.close();

    store.dispatch(producerActions.removeProducer(this._micProducer.id));

    try {
      await this.sendRequest("closeProducer", {
        producerId: this._micProducer.id,
      });
    } catch (error) {
      logger.error('disableMic() [error:"%o"]', error);
    }

    this._micProducer = null;

    store.dispatch(meActions.setAudioInProgress(false));
  }

  async enableScreenSharing() {
    if (this._screenSharingProducer) return;

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.error("enableScreenSharing() | cannot produce video");

      return;
    }

    let track;

    store.dispatch(meActions.setScreenShareInProgress(true));

    try {
      const available = this._screenSharing.isScreenShareAvailable();

      if (!available) throw new Error("screen sharing not available");

      logger.debug("enableScreenSharing() | calling getUserMedia()");

      const conf = getConferenceConfig();
      const stream = await this._screenSharing.start({
        width: conf.screenShareWidth,
        height: conf.screenShareHeight,
        frameRate: conf.screenShareFrameRate,
      });

      track = stream.getVideoTracks()[0];
      let audioTrack = stream.getAudioTracks()[0];
      const preferredCodec = pickPreferredVideoCodec(
        this._mediasoupDevice,
        conf.preferredVideoCodec
      );

      if (this._useSharingSimulcast) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find(
          (c) => c.kind === "video"
        );

        let encodings;

        if (firstVideoCodec.mimeType.toLowerCase() === "video/vp9") {
          encodings = VIDEO_SVC_ENCODINGS;
        } else {
          if ("simulcastEncodings" in window.config) {
            encodings = window.config.simulcastEncodings.map((encoding) => ({
              ...encoding,
              dtx: true,
            }));
          } else {
            encodings = VIDEO_SIMULCAST_ENCODINGS.map((encoding) => ({
              ...encoding,
              dtx: true,
            }));
          }
        }

        this._screenSharingProducer = await this._sendTransport.produce({
          track,
          encodings,
          codec: preferredCodec,
          codecOptions: {
            videoGoogleStartBitrate: 1000,
          },
          appData: {
            source: "screen",
          },
        });
      } else {
        if (audioTrack) {
          this._sendTransport.produce({
            track: audioTrack,
            appData: {
              source: "mic",
            },
          });
        }

        this._screenSharingProducer = await this._sendTransport.produce({
          track,
          codec: preferredCodec,
          appData: {
            source: "screen",
          },
        });
      }

      store.dispatch(
        producerActions.addProducer({
          id: this._screenSharingProducer.id,
          deviceLabel: "screen",
          source: "screen",
          paused: this._screenSharingProducer.paused,
          track: this._screenSharingProducer.track,
          rtpParameters: this._screenSharingProducer.rtpParameters,
          codec: this._screenSharingProducer.rtpParameters.codecs[0].mimeType.split(
            "/"
          )[1],
        })
      );

      this._screenSharingProducer.on("transportclose", () => {
        this._screenSharingProducer = null;
      });

      this._screenSharingProducer.on("trackended", () => {
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.screenSharingDisconnected",
              defaultMessage: "Screen sharing disconnected",
            }),
          })
        );

        this.disableScreenSharing().catch(() => {});
      });

      logger.debug("enableScreenSharing() succeeded");
    } catch (error) {
      logger.error("enableScreenSharing() failed: %o", error);
      if (!this._call)
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.screenSharingError",
              defaultMessage: "An error occured while accessing your screen",
            }),
          })
        );

      if (track) track.stop();
    }

    store.dispatch(meActions.setScreenShareInProgress(false));
  }

  async disableScreenSharing() {
    logger.debug("disableScreenSharing()");

    if (!this._screenSharingProducer) return;

    store.dispatch(meActions.setScreenShareInProgress(true));

    this._stopProducerTrack(this._screenSharingProducer);
    this._screenSharingProducer.close();

    store.dispatch(
      producerActions.removeProducer(this._screenSharingProducer.id)
    );

    this.endRemoteControl("screen_closed").catch(() => {});

    try {
      await this.sendRequest("closeProducer", {
        producerId: this._screenSharingProducer.id,
      });
    } catch (error) {
      logger.error('disableScreenSharing() [error:"%o"]', error);
    }

    this._screenSharingProducer = null;

    store.dispatch(meActions.setScreenShareInProgress(false));
  }

  async enableWebcam() {
    if (this._webcamProducer) return;

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.error("enableWebcam() | cannot produce video");

      return;
    }

    let track;

    store.dispatch(meActions.setWebcamInProgress(true));

    try {
      const deviceId = await this._getWebcamDeviceId();

      const device = this._webcams[deviceId];
      const resolution = store.getState().settings.resolution;

      if (!device) throw new Error("no webcam devices");

      logger.debug(
        "_setWebcamProducer() | new selected webcam [device:%o]",
        device
      );

      logger.debug("_setWebcamProducer() | calling getUserMedia()");

      const conf = getConferenceConfig();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { ideal: deviceId },
          ...(VIDEO_CONSTRAINS[resolution] || VIDEO_CONSTRAINS.veryhigh),
          frameRate: conf.webcamFrameRate,
        },
      });
      this._localStreams.push(stream);

      track = stream.getVideoTracks()[0];
      const preferredCodec = pickPreferredVideoCodec(
        this._mediasoupDevice,
        conf.preferredVideoCodec
      );

      if (this._useSimulcast) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find(
          (c) => c.kind === "video"
        );

        let encodings;

        if (firstVideoCodec.mimeType.toLowerCase() === "video/vp9")
          encodings = VIDEO_KSVC_ENCODINGS;
        else {
          if ("simulcastEncodings" in window.config)
            encodings = window.config.simulcastEncodings;
          else encodings = VIDEO_SIMULCAST_ENCODINGS;
        }

        this._webcamProducer = await this._sendTransport.produce({
          track,
          encodings,
          codec: preferredCodec,
          codecOptions: {
            videoGoogleStartBitrate: 1000,
          },
          appData: {
            source: "webcam",
          },
        });
      } else {
        this._webcamProducer = await this._sendTransport.produce({
          track,
          codec: preferredCodec,
          appData: {
            source: "webcam",
          },
        });
      }

      store.dispatch(
        producerActions.addProducer({
          id: this._webcamProducer.id,
          deviceLabel: device.label,
          source: "webcam",
          paused: this._webcamProducer.paused,
          track: this._webcamProducer.track,
          rtpParameters: this._webcamProducer.rtpParameters,
          codec: this._webcamProducer.rtpParameters.codecs[0].mimeType.split(
            "/"
          )[1],
        })
      );

      store.dispatch(settingsActions.setSelectedWebcamDevice(deviceId));

      await this._updateWebcams();

      this._webcamProducer.on("transportclose", () => {
        this._webcamProducer = null;
      });

      this._webcamProducer.on("trackended", () => {
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.cameraDisconnected",
              defaultMessage: "Camera disconnected",
            }),
          })
        );

        this.disableWebcam().catch(() => {});
      });

      logger.debug("_setWebcamProducer() succeeded");
    } catch (error) {
      logger.error("_setWebcamProducer() failed:%o", error);
      if (!this._call)
        store.dispatch(
          requestActions.notify({
            type: "error",
            text: intl.formatMessage({
              id: "devices.cameraError",
              defaultMessage: "An error occured while accessing your camera",
            }),
          })
        );

      if (track) track.stop();
    }

    store.dispatch(meActions.setWebcamInProgress(false));
  }

  async disableWebcam() {
    logger.debug("disableWebcam()");

    if (!this._webcamProducer) return;

    store.dispatch(meActions.setWebcamInProgress(true));

    this._stopProducerTrack(this._webcamProducer);
    this._webcamProducer.close();

    store.dispatch(producerActions.removeProducer(this._webcamProducer.id));

    try {
      await this.sendRequest("closeProducer", {
        producerId: this._webcamProducer.id,
      });
    } catch (error) {
      logger.error('disableWebcam() [error:"%o"]', error);
    }

    this._webcamProducer = null;

    store.dispatch(meActions.setWebcamInProgress(false));
  }

  async _updateAudioDevices() {
    logger.debug("_updateAudioDevices()");

    // Reset the list.
    this._audioDevices = {};

    try {
      logger.debug("_updateAudioDevices() | calling enumerateDevices()");

      const devices = await navigator.mediaDevices.enumerateDevices();

      for (const device of devices) {
        if (device.kind !== "audioinput") continue;

        this._audioDevices[device.deviceId] = device;
      }

      store.dispatch(meActions.setAudioDevices(this._audioDevices));
    } catch (error) {
      logger.error("_updateAudioDevices() failed:%o", error);
    }
  }

  async _updateWebcams() {
    logger.debug("_updateWebcams()");

    // Reset the list.
    this._webcams = {};

    try {
      logger.debug("_updateWebcams() | calling enumerateDevices()");

      const devices = await navigator.mediaDevices.enumerateDevices();

      for (const device of devices) {
        if (device.kind !== "videoinput") continue;

        this._webcams[device.deviceId] = device;
      }

      store.dispatch(meActions.setWebcamDevices(this._webcams));
    } catch (error) {
      logger.error("_updateWebcams() failed:%o", error);
    }
  }

  async _getAudioDeviceId() {
    logger.debug("_getAudioDeviceId()");

    try {
      logger.debug("_getAudioDeviceId() | calling _updateAudioDeviceId()");

      await this._updateAudioDevices();

      const { selectedAudioDevice } = store.getState().settings;

      if (selectedAudioDevice && this._audioDevices[selectedAudioDevice])
        return selectedAudioDevice;
      else {
        const audioDevices = Object.values(this._audioDevices);

        return audioDevices[0] ? audioDevices[0].deviceId : null;
      }
    } catch (error) {
      logger.error("_getAudioDeviceId() failed:%o", error);
    }
  }

  async _getWebcamDeviceId() {
    logger.debug("_getWebcamDeviceId()");

    try {
      logger.debug("_getWebcamDeviceId() | calling _updateWebcams()");

      await this._updateWebcams();

      const { selectedWebcam } = store.getState().settings;

      if (selectedWebcam && this._webcams[selectedWebcam])
        return selectedWebcam;
      else {
        const webcams = Object.values(this._webcams);

        return webcams[0] ? webcams[0].deviceId : null;
      }
    } catch (error) {
      logger.error("_getWebcamDeviceId() failed:%o", error);
    }
  }

  async _updateAudioOutputDevices() {
    logger.debug("_updateAudioOutputDevices()");

    // Reset the list.
    this._audioOutputDevices = {};

    try {
      logger.debug("_updateAudioOutputDevices() | calling enumerateDevices()");

      const devices = await navigator.mediaDevices.enumerateDevices();

      for (const device of devices) {
        if (device.kind !== "audiooutput") continue;

        this._audioOutputDevices[device.deviceId] = device;
      }

      store.dispatch(meActions.setAudioOutputDevices(this._audioOutputDevices));
    } catch (error) {
      logger.error("_updateAudioOutputDevices() failed:%o", error);
    }
  }

  dispatchNotification(id, type, defaultMessage) {
    store.dispatch(
      requestActions.notify({
        type: type,
        text: intl.formatMessage({
          id: id,
          defaultMessage: defaultMessage,
        }),
      })
    );
  }

  getDeviceFlag() {
    return this._device.flag;
  }

  getRoomId() {
    return this._roomId;
  }

  async enableScreenSharingForRecording(liveStreaming) {
    let track;

    try {
      const available = this._screenSharingForRecorder.isScreenShareAvailable();

      if (!available) throw new Error("screen sharing not available");

      logger.debug("enableScreenSharing() | calling getUserMedia()");

      const stream = await this._screenSharingForRecorder.start({
        width: 1280,
        height: 720,
        frameRate: 15,
      });

      this._liveStreaming = liveStreaming;
      this.appendRecording(stream, true, liveStreaming);

      track = stream.getVideoTracks()[0];

      track.addEventListener("ended", () => {
        if (liveStreaming) {
          store.dispatch(roomActions.setLiveStreamingNotificationStatus(false));
          this.sendRequest("setLiveStreamingNotificationStatus", {
            liveStreamingNotificationStatus: false,
          });
        } else {
          store.dispatch(roomActions.setRecordingNotificationStatus(false));
          this.sendRequest("setRecordingNotificationStatus", {
            recordingNotificationStatus: false,
          });
        }
        this.stopRecording();
      });

      if (liveStreaming) {
        store.dispatch(roomActions.setLiveStreamingNotificationStatus(true));
        this.sendRequest("setLiveStreamingNotificationStatus", {
          liveStreamingNotificationStatus: true,
        });
      } else {
        store.dispatch(roomActions.setRecordingNotificationStatus(true));
        await this.sendRequest("setRecordingNotificationStatus", {
          recordingNotificationStatus: true,
        });
      }

      logger.debug("enableScreenSharing() succeeded");
    } catch (error) {
      logger.error("enableScreenSharing() failed: %o", error);

      if (track) track.stop();
    }
  }

  _recorder = undefined;
  blobs = [];
  recordStreams = [];

  setPeerId(id) {
    this._peerId = id;
    store.dispatch(meActions.setMe({ peerId: id }));
  }

  setRoomActiveSpeaker(peerId) {
    this._roomActiveSpeaker = peerId;
    store.dispatch(roomActions.setRoomActiveSpeaker(peerId));
    if (peerId && peerId !== this._peerId)
      this._spotlights.handleActiveSpeaker(peerId);
  }

  getRoomActiveSpeaker() {
    return this._roomActiveSpeaker;
  }

  getStore() {
    return store;
  }

  setIntl(intlObj) {
    intl = intlObj;
  }

  getCreatorId() {
    return this._creatorId;
  }

  startRecording(liveStreamingParams) {
    if (liveStreamingParams) {
      if (this._liveStreamingSocket) this._liveStreamingSocket.close();
      this._liveStreamingSocket = io(window.config.liveStreamServiceUrl, {
        transports: ["websocket"],
        pingInterval: 45000,
        pingTimeout: 15000,
        query: { framespersecond: 15, audioBitrate: 48000 },
        reconnection: true,
        reconnectionDelay: 1000,
        secure: true,
        timeout: 15000,
      });

      this._liveStreamingSocket.on("connect", () => {
        logger.debug("live streaming socket connected");
        this._liveStreamingSocket.emit(
          "config_rtmpDestination",
          liveStreamingParams?.url
        );
        this._liveStreamingSocket.emit("start", "start");
        this.enableScreenSharingForRecording(true);
      });
    } else this.enableScreenSharingForRecording(false);
  }

  appendRecording(stream, start = false, liveStreaming = false) {
    this.recordStreams.push(stream);
    if (!start && !this._recording) {
      return;
    }
    if (!this._recorder) {
      this._recorder = RecordRTC(this.recordStreams, {
        type: "video",
        video: {
          width: 1280,
          height: 720,
        },
        /*frameRate: 15,*/
        audioBitsPerSecond: 48000,
        videoBitsPerSecond: 3000000,
        getNativeBlob: true,
        timeSlice: 1000,
        mimeType: "video/webm",
        ondataavailable: (blob) => {
          if (this._liveStreaming && this._liveStreamingSocket)
            this._liveStreamingSocket.emit("binarystream", blob);
        },
      });
      this._recorder.startRecording();
      this._recording = true;
      if (!liveStreaming)
        store.dispatch(roomActions.setRoomRecordingState("recording"));
    } else if (stream) {
      this._recorder.getInternalRecorder().getMixer().appendStreams([stream]);
    }
  }

  startRemoteRecording() {
    if (this.recordStreams.length === 0) {
      setTimeout(() => {
        this.startRemoteRecording();
      }, 1000);
      return;
    }
    if (this._liveStreamingSocket) this._liveStreamingSocket.close();
    this._liveStreamingSocket = io(window.config.liveStreamServiceUrl, {
      transports: ["websocket"],
      pingInterval: 45000,
      pingTimeout: 15000,
      query: { framespersecond: 15, audioBitrate: 48000 },
      reconnection: true,
      reconnectionDelay: 1000,
      secure: true,
      timeout: 15000,
    });
    this._liveStreamingSocket.on("connect", () => {
      logger.debug("live streaming socket connected");
      this._liveStreamingSocket.emit("startremoterecording", this._roomId);
      if (!this._recorder) {
        this._recorder = RecordRTC(this.recordStreams, {
          type: "audio",
          audioBitsPerSecond: 48000,
          getNativeBlob: true,
          timeSlice: 1000,
          mimeType: "audio/mp3",
          ondataavailable: (blob) => {
            if (this._liveStreamingSocket)
              this._liveStreamingSocket.emit("binaryrecordingstream", blob);
          },
        });
        this._recorder.startRecording();
        this._recording = true;
        store.dispatch(roomActions.setRoomRecordingState("recording"));
        store.dispatch(roomActions.setRecordingNotificationStatus(true));
      }
    });
  }

  pauseRecording() {
    if (!this._recorder) return;
    store.dispatch(roomActions.setRoomRecordingState("pause"));
    this._recorder.getInternalRecorder().pause();
    store.dispatch(roomActions.setRecordingNotificationStatus(false));
    this.sendRequest("setRecordingNotificationStatus", {
      recordingNotificationStatus: false,
    });
  }

  resumeRecording() {
    if (!this._recorder) return;
    store.dispatch(roomActions.setRoomRecordingState("recording"));
    this._recorder.getInternalRecorder().resume();
    store.dispatch(roomActions.setRecordingNotificationStatus(true));
    this.sendRequest("setRecordingNotificationStatus", {
      recordingNotificationStatus: true,
    });
  }

  stopRecording() {
    store.dispatch(roomActions.setRoomRecordingState("stop"));
    if (this._liveStreamingSocket) this._liveStreamingSocket.close();
    this._screenSharingForRecorder.stop();
    this._recording = false;
    if (!this._recorder) return;
    let blobs = this.blobs;
    this._recorder.stopRecording(() => {
      if (!this._liveStreaming) {
        RecordRTC.getSeekableBlob(this._recorder.getBlob(), (seekableBlob) => {
          blobs.push(seekableBlob);
          const webm = blobs.reduce(
            (a, b) => new Blob([a, b], { type: "video/webm" })
          );
          saveAs(webm, this._roomId + ".webm");
        });
      }
      this._recorder.reset();
      this._recorder = null;
      this.blobs = null;
      this.blobs = [];
      this._liveStreaming = false;
    });
    store.dispatch(roomActions.setRecordingNotificationStatus(false));
    this.sendRequest("setRecordingNotificationStatus", {
      recordingNotificationStatus: false,
    });

    store.dispatch(roomActions.setLiveStreamingNotificationStatus(false));
    this.sendRequest("setLiveStreamingNotificationStatus", {
      liveStreamingNotificationStatus: false,
    });
  }
}
